import * as React from "react";
import Toggle from "react-toggle";
import Select from "react-select";
import { Modal, Tabs, Tab, FormGroup, Form, Dropdown } from "react-bootstrap";
import { connect } from "react-redux";
import type { Dispatch } from "redux";
import { FormattedMessage } from "react-intl";
import {
    RecommendPlanLogic,
    BackwardPlanLogic,
    SequencingPlanLogic,
} from "@/modules/pg/shared/planningTable/reducers/logic/businessLogic";
import {
    SET_RECOMMEND_PLAN_ERROR,
    SET_RECOMMEND_PLAN_MODAL,
    SET_RECOMMEND_WARNINGS,
} from "@/modules/pg/shared/planningTable/reducers/planningTable";
import * as BusinessLogic from "@/lib/businessLogic";
import local_settings from "@/lib/localSettings";
import {
    LINE_TAGS,
    LINE_GROUP_TAGS,
    LINE_GROUP_TAGS_ACCESS,
    ORDER_TAGS_ACCESS,
    PLAN_HORIZON_UNIT_AUTOMATIC_SCHEDULING,
    PLANNING_META_OPTIMIZATION,
    PlanningMetaOptimizationEnum,
} from "@/lib/ManufacturingTags.generated";
import { ORDER_TYPE, PLANNING_SCHEDULE_STRATEGY } from "@/lib/ManufacturingConsts.generated";
import {
    isNumber,
    ILabelValuePair,
    SHIFT_CONSTS,
    dateFromWeekAndShift,
    addHours,
    niceDate,
    shiftNumber,
    TIME_RANGES,
    range,
} from "@/lib/util";
import ErrorComponent from "@/modules/shared/ui/ErrorComponent";
import { translate } from "@/modules/shared/IntlProviderWrapper";
import { LinesOrdersLogic } from "@/modules/pg/shared/planningTable/reducers/logic/businessLogic";
import { getBackend } from "@/lib/backend/backend2";
import ConfirmationModal from "@/modules/shared/ui/ConfirmationModal";
import SaveConfigurationModal from "../modals/SaveConfigurationModal";
import Authorization from "@/modules/shared/Authorization";
import * as Auth from "@/lib/auth";
import Tooltip from "@/modules/shared/ui/Tooltip";
import DateTimePicker from "@/modules/shared/ui/datetime/DateTimePicker";
import MultiSelect from "@/modules/shared/ui/forms/MultiSelect";

import * as t from "@/lib/backend/manufacturing2.generated.types";
import type { OrderTypeEnum } from "@/lib/ManufacturingConsts.generated";
import * as t2 from "@/lib/simulationReportModels";
import type { LineOrders, IOrderProducedModelEx } from "@/modules/pg/shared/planningTable/reducers/linesOrders";
import type {
    RunRecommendPlanParameters,
    RunBackwardPlanParameters,
} from "@/modules/pg/shared/planningTable/reducers/logic/businessLogic";
import type { ReduxState } from "@/modules/pg/shared/planningTable/reducers/reducers";
import type {
    RecommendPlanParamsAndCriteria,
    RecommendPlanActions,
} from "@/modules/pg/shared/planningTable/reducers/planningTable/recommendPlan";
import type { PlanningTableActionsTypes } from "@/modules/pg/shared/planningTable/reducers/planningTable";
import type { LineOrdersActionsTypes } from "@/modules/pg/shared/planningTable/reducers/linesOrders";
import type { SetSkipStockReport } from "@/modules/pg/shared/planningTable/reducers/gantt/properties";

import "./RecommendPlan.scss";

export const PLANNING_MODEL_PARAMS_DEFAULT = {
    can_change_workorder_line: true,
};

export const PLANNING_PARAMS_DEFAULT = {
    beam_size: 3,
    max_order_end_date: TIME_RANGES.DAY * 5,
    horizon_limit: TIME_RANGES.DAY * 3,
};

type PhaseOption = "1" | "2" | "3" | "4";
type AlternativesStrategyOption = "only-primary" | "all-lines-keep-prefered" | "all-lines-no-prefered";

type ActionTypes = PlanningTableActionsTypes | LineOrdersActionsTypes | RecommendPlanActions | SetSkipStockReport;

type OwnProps = {
    plant_uuid: string;
    line_group_uuid: string;
};

type Props = OwnProps & ReduxProps;

type WarningDetailsProps = {
    warning: t.IPlanWarning;
    container_ref: Element | null;
};

type State = {
    configuration: string;
    configurations: t.ISchedulingConfiguration[];
    recommend_plan_parameters: RecommendPlanParamsAndCriteria;
    show_delete_modal: boolean;
    show_file_name_modal: boolean;
    selected_tab: string;
    selected_lines: Set<string>;
};

/**
 * Filter function for line options in the lines select component.
 * @param {ILabelValuePair<string>} option - The option to filter.
 * @param {string} input_value - The input value to filter by.
 * @returns {boolean} - `true` if the option matches the input value, `false` otherwise.
 */
const filterLineOption = (option: ILabelValuePair<string>, input_value: string): boolean => {
    return option.label.toLowerCase().includes(input_value.toLowerCase());
};

/**
 * Generate an array of select options for numbers in a range.
 * @param {number} min - The minimum number in the range (inclusive).
 * @param {number} max - The maximum number in the range (exclusive).
 * @returns {ILabelValuePair<number>[]} - Array of options with label and value properties.
 */
const getNumberOptions = (min: number, max: number): ILabelValuePair<number>[] => {
    return range(min, max).map(value => ({
        label: `${value}`,
        value,
    }));
};

const getStrategyPrefix = (strategy: string): string => {
    switch (strategy) {
        case PLANNING_SCHEDULE_STRATEGY.rectify:
            return "rectify";
        case PLANNING_SCHEDULE_STRATEGY.backward_planning:
            return "backward";
        case PLANNING_SCHEDULE_STRATEGY.sequencing_planning:
            return "sequencing";
        default:
            return "recommend";
    }
};

const getRecommendPlanInitialConfiguration = (line_group_uuid: string): RecommendPlanParamsAndCriteria => {
    const line_group_tags = BusinessLogic.getLineGroupTags(line_group_uuid);
    let recommend_strategy = PLANNING_SCHEDULE_STRATEGY.BFS;
    if (Auth.getOrgData().code == "agrana") {
        recommend_strategy = PLANNING_SCHEDULE_STRATEGY.sequencing_planning;
    }
    return {
        recommend_param_beam_size: PLANNING_PARAMS_DEFAULT.beam_size,
        recommend_param_can_change_workorder_line: PLANNING_MODEL_PARAMS_DEFAULT.can_change_workorder_line,
        recommend_param_track_input_material: BusinessLogic.getLineGroupTagBool(
            line_group_uuid,
            LINE_GROUP_TAGS.planning_track_input_material,
            true
        ),
        recommend_param_use_productions_of_unscheduled_operations: true,
        recommend_param_use_requirements_from_unscheduled_operations: true,
        recommend_param_use_safety_stock: true,
        recommend_param_consider_alternative_lines: false,
        recommend_param_consider_priority: false,
        recommend_param_consider_people_presences: false,
        recommend_param_consider_people_competences: false,
        recommend_param_customer_priority: false,
        recommend_param_include_plan_orders: true,
        recommend_param_include_unscheduled_orders: true,
        recommend_param_freeze_work_orders: true,
        recommend_param_plan_after_frozen_orders: false,
        recommend_param_freeze_shifts: 0,
        recommend_param_time_horizont_shifts: 2 * 21,
        recommend_param_unscheduled_offset_shifts: 2 * 21,
        recommend_param_meta_optimization: PLANNING_META_OPTIMIZATION.none,
        recommend_param_override_shifts: undefined,
        recommend_param_need_score_factor: 0,
        recommend_param_setup_code_change_factor:
            LINE_GROUP_TAGS_ACCESS.planning_setup_code_change_factor(line_group_tags),
        recommend_param_work_duration_factor: LINE_GROUP_TAGS_ACCESS.planning_work_duration_factor(line_group_tags),
        recommend_param_max_order_end_date: PLANNING_PARAMS_DEFAULT.max_order_end_date,
        recommend_param_horizon_limit: PLANNING_PARAMS_DEFAULT.horizon_limit,
        recommend_param_alternatives_strategy: "all-lines-keep-prefered",
        recommend_param_include_history: false,
        recommend_param_consider_optimum_batch: false,
        recommend_param_run_until_phase: "4",
        recommend_criteria_delays: 3,
        recommend_criteria_tool_changes: 2,
        recommend_criteria_intermediate_stock: 0,
        recommend_strategy,
    };
};

const getRecommendPlanConfiguration = (line_group_uuid: string, strategy: string): RecommendPlanParamsAndCriteria => {
    const saved_recommend_plan_state = local_settings.getItem(`${getStrategyPrefix(strategy)}-plan-state`);
    if (saved_recommend_plan_state) {
        const recommend_plan_state = JSON.parse(saved_recommend_plan_state);
        if (recommend_plan_state["recommend_param_time_horizont_shifts"] == undefined) {
            recommend_plan_state["recommend_param_time_horizont_shifts"] =
                (recommend_plan_state["recommend_param_time_horizont_weeks"] || 2) * 21;
        }

        const meta_optimization_param = BusinessLogic.getLineGroupTagBool(
            line_group_uuid,
            LINE_GROUP_TAGS.planning_meta_optimization_param,
            false
        );
        if (!meta_optimization_param) {
            recommend_plan_state["recommend_param_meta_optimization"] = PLANNING_META_OPTIMIZATION.none;
            recommend_plan_state["recommend_param_override_shifts"] = undefined;
        } else if (recommend_plan_state.recommend_param_meta_optimization === PLANNING_META_OPTIMIZATION.none) {
            recommend_plan_state["recommend_param_override_shifts"] = undefined;
        } else if (
            recommend_plan_state.recommend_param_meta_optimization === PLANNING_META_OPTIMIZATION.max_resources
        ) {
            recommend_plan_state["recommend_param_override_shifts"] = 0;
        } else {
            recommend_plan_state["recommend_param_override_shifts"] = Math.max(
                1,
                Math.min(4, recommend_plan_state.recommend_param_override_shifts || 1)
            );
        }

        return {
            ...getRecommendPlanInitialConfiguration(line_group_uuid),
            ...recommend_plan_state,
        };
    }

    return getRecommendPlanInitialConfiguration(line_group_uuid);
};

const setRecommendPlanConfiguration = (
    _line_group_uuid: string,
    strategy: string,
    recommend_plan_state: RecommendPlanParamsAndCriteria
) => {
    local_settings.setItem(`${getStrategyPrefix(strategy)}-plan-state`, JSON.stringify(recommend_plan_state));
};

const isWeekUnit = (line_group_uuid: string): boolean => {
    return (
        BusinessLogic.getLineGroupTagStr(
            line_group_uuid,
            LINE_GROUP_TAGS.planning_time_horizont_unit,
            PLAN_HORIZON_UNIT_AUTOMATIC_SCHEDULING.week_unit
        ) === PLAN_HORIZON_UNIT_AUTOMATIC_SCHEDULING.week_unit
    );
};

const getRecommendPlanConfigurationValue = <K extends keyof RecommendPlanParamsAndCriteria>(
    line_group_uuid: string,
    strategy: string,
    key: K
): RecommendPlanParamsAndCriteria[K] => {
    const recommend_plan_state = getRecommendPlanConfiguration(line_group_uuid, strategy);
    return recommend_plan_state[key];
};

const setRecommendPlanConfigurationValue = <K extends keyof RecommendPlanParamsAndCriteria>(
    line_group_uuid: string,
    strategy: string,
    key: K,
    value?: RecommendPlanParamsAndCriteria[K]
) => {
    const recommend_plan_state = getRecommendPlanConfiguration(line_group_uuid, strategy);

    const is_week_unit = isWeekUnit(line_group_uuid);
    if (is_week_unit && key === "recommend_param_time_horizont_shifts" && typeof value === "number") {
        value = (value * 21) as RecommendPlanParamsAndCriteria[K];
    }
    recommend_plan_state[key] = value as RecommendPlanParamsAndCriteria[K];
    local_settings.setItem(`${getStrategyPrefix(strategy)}-plan-state`, JSON.stringify(recommend_plan_state));
};

const getConfigurationName = (strategy: string): string => {
    return local_settings.getItem(`${getStrategyPrefix(strategy)}-configuration-name`) || "";
};

const setConfigurationName = (strategy: string, name: string) => {
    local_settings.setItem(`${getStrategyPrefix(strategy)}-configuration-name`, name);
};

const SplitOperationWarningDetails = (props: WarningDetailsProps) => {
    const { explanation } = props.warning;
    const shift_diffs: LineOrders[] = explanation.extra_data.shift_diffs || [];

    let description = "";
    if (shift_diffs.length > 0) {
        description += `${translate(
            "Manufacturing.Planning.Explanation.split_operation_shift_inconsistencies",
            "Operation was split due to inconsistently configured shifts on related line"
        )}.`;
    } else {
        description += `${translate(
            "Manufacturing.Planning.Explanation.split_operation_unknown",
            "Operation was split due to unknown reason"
        )}.`;
    }

    return (
        <div className="split-operation-warning-details">
            {description}{" "}
            {shift_diffs.length > 0 && (
                <Tooltip
                    class_name="split-operation-warning-details__tooltip"
                    trigger="click"
                    container={props.container_ref}
                    content={
                        <>
                            <p className="px-2 pb-2 mb-0">
                                {translate(
                                    "Manufacturing.Planning.differences_in_shift_settings",
                                    "Differences in shift settings"
                                )}
                            </p>
                            {shift_diffs.map(line_orders => (
                                <React.Fragment key={line_orders.line_uuid}>
                                    <p className="p-2 mb-0">{line_orders.line_title}</p>
                                    <table className="table mb-0">
                                        <thead>
                                            <tr>
                                                <th>{translate("common.date", "Date")}</th>
                                                <th className="text-right">{translate("common.shift", "Shift")}</th>
                                                <th>
                                                    {translate("Manufacturing.Planning.shift_enabled", "Shift enabled")}
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {line_orders.shifts.map(shift => {
                                                const date = dateFromWeekAndShift(
                                                    shift.week,
                                                    shift.year,
                                                    shift.shift_number
                                                );
                                                const shift_number = shiftNumber(date);
                                                return (
                                                    <tr key={shift.shift_number}>
                                                        <td>
                                                            {niceDate(
                                                                dateFromWeekAndShift(
                                                                    shift.week,
                                                                    shift.year,
                                                                    shift.shift_number
                                                                )
                                                            )}
                                                        </td>
                                                        <td className="text-right">{shift_number.shift_day + 1}</td>
                                                        <td>
                                                            {shift.enabled
                                                                ? translate("common.yes", "Yes")
                                                                : translate("common.no", "No")}
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </React.Fragment>
                            ))}
                        </>
                    }
                >
                    <button className="btn btn-short btn-flat-light fas fa-info-circle p-1" />
                </Tooltip>
            )}
        </div>
    );
};

const selectInputText = (event: React.SyntheticEvent<HTMLInputElement>) => {
    event.currentTarget.select();
};

class RecommendPlan extends React.Component<Props, State> {
    selectAlternativesStrategyOptions: ILabelValuePair<AlternativesStrategyOption>[] = [
        {
            value: "only-primary",
            label: translate("Manufacturing.Planning.AlternativesStrategy.only_primary", "Only primary lines"),
        },
        {
            value: "all-lines-keep-prefered",
            label: translate(
                "Manufacturing.Planning.AlternativesStrategy.all_lines_keep_prefered",
                "All lines (keep preferred)"
            ),
        },
        {
            value: "all-lines-no-prefered",
            label: translate(
                "Manufacturing.Planning.AlternativesStrategy.all_lines_no_prefered",
                "All lines (no preference)"
            ),
        },
    ];

    selectRunUntilPhaseOptions: ILabelValuePair<PhaseOption>[] = [
        {
            value: "1",
            label: translate("Manufacturing.Planning.RunUntilPhase.phase_1", "Phase 1 (optimize campaigns)"),
        },
        {
            value: "2",
            label: translate("Manufacturing.Planning.RunUntilPhase.phase_2", "Phase 2 (first non-primary assignment)"),
        },
        {
            value: "3",
            label: translate("Manufacturing.Planning.RunUntilPhase.phase_3", "Phase 3 (second non-primary assignment)"),
        },
        {
            value: "4",
            label: translate("Manufacturing.Planning.RunUntilPhase.phase_4", "Phase 4 (all steps)"),
        },
    ];

    modal_ref: Element | null = null;

    constructor(props: Props) {
        super(props);
        this.state = {
            configuration: "",
            configurations: [],
            recommend_plan_parameters: getRecommendPlanConfiguration(
                props.line_group_uuid,
                PLANNING_SCHEDULE_STRATEGY.BFS
            ),
            show_delete_modal: false,
            show_file_name_modal: false,
            selected_tab: "parameters",
            selected_lines: new Set(props.selected_line_uuids),
        };
    }

    async componentDidMount() {
        await this.loadConfigurations();
    }

    async componentDidUpdate(prev_props: Props) {
        if (prev_props.line_group_uuid !== this.props.line_group_uuid) {
            this.setState({
                recommend_plan_parameters: getRecommendPlanConfiguration(
                    this.props.line_group_uuid,
                    this.state.recommend_plan_parameters.recommend_strategy
                ),
            });
            await this.loadConfigurations();
        }

        if (this.props.recommend_plan_params !== prev_props.recommend_plan_params) {
            if (this.props.recommend_plan_params !== null) {
                const recommend_plan_parameters = { ...this.props.recommend_plan_params };
                this.setState({ recommend_plan_parameters });
                setRecommendPlanConfiguration(
                    this.props.line_group_uuid,
                    recommend_plan_parameters.recommend_strategy,
                    recommend_plan_parameters
                );
            } else {
                this.setState({
                    recommend_plan_parameters: getRecommendPlanConfiguration(
                        this.props.line_group_uuid,
                        this.state.recommend_plan_parameters.recommend_strategy
                    ),
                });
            }
        }

        if (prev_props.selected_line_uuids !== this.props.selected_line_uuids) {
            this.setState({
                selected_lines: new Set(this.props.selected_line_uuids),
            });
        }
    }

    getSelectedAlternativesStrategy = (): ILabelValuePair | void => {
        return this.selectAlternativesStrategyOptions.find(
            option => option.value === this.state.recommend_plan_parameters.recommend_param_alternatives_strategy
        );
    };

    getSelectedRunUntilPhase = (): ILabelValuePair | void => {
        return this.selectRunUntilPhaseOptions.find(
            option => option.value === this.state.recommend_plan_parameters.recommend_param_run_until_phase
        );
    };

    getShowModal = (): boolean => {
        return this.props.show_recommend_param_dialog;
    };

    getRecommendIsRunning = (): boolean => {
        return this.props.recommend_running;
    };

    hideConfigurationModal = () => {
        this.props.reduxDispatch({
            type: SET_RECOMMEND_PLAN_MODAL,
            data: {
                show_recommend_param_dialog: false,
            },
        });
        this.props.reduxDispatch({ type: SET_RECOMMEND_WARNINGS, data: [] });
        this.props.reduxDispatch({ type: SET_RECOMMEND_PLAN_ERROR, data: null });
    };

    showConfigurationModal = () => {
        this.props.reduxDispatch({
            type: SET_RECOMMEND_PLAN_MODAL,
            data: {
                show_recommend_param_dialog: true,
            },
        });
    };

    showRecommendModal = (config?: t.ISchedulingConfiguration, autorun?: boolean) => {
        if (config) {
            this.setStateFromConfiguration(config, () => {
                this.showConfigurationModal();
                if (autorun) {
                    this.recommendPlan();
                }
            });

            return;
        }

        const strategy = PLANNING_SCHEDULE_STRATEGY.BFS;
        setRecommendPlanConfigurationValue(this.props.line_group_uuid, strategy, "recommend_strategy", strategy);
        this.resetConfiguration(strategy);
        this.setState(
            {
                recommend_plan_parameters: getRecommendPlanConfiguration(this.props.line_group_uuid, strategy),
            },
            () => {
                this.showConfigurationModal();
                if (autorun) {
                    this.recommendPlan();
                }
            }
        );
    };

    showBackwardPlanningModal = (config?: t.ISchedulingConfiguration, autorun?: boolean) => {
        if (config) {
            this.setStateFromConfiguration(config, () => {
                this.showConfigurationModal();
                if (autorun) {
                    this.backwardPlanning();
                }
            });

            return;
        }

        const strategy = PLANNING_SCHEDULE_STRATEGY.backward_planning;
        setRecommendPlanConfigurationValue(this.props.line_group_uuid, strategy, "recommend_strategy", strategy);
        this.resetConfiguration(strategy);
        this.setState(
            {
                recommend_plan_parameters: getRecommendPlanConfiguration(this.props.line_group_uuid, strategy),
            },
            () => {
                this.showConfigurationModal();
                if (autorun) {
                    this.backwardPlanning();
                }
            }
        );
    };

    showSequencingPlanningModal = (config?: t.ISchedulingConfiguration, autorun?: boolean) => {
        if (config) {
            this.setStateFromConfiguration(config, () => {
                this.showConfigurationModal();
                if (autorun) {
                    this.sequencingPlanning();
                }
            });

            return;
        }

        const strategy = PLANNING_SCHEDULE_STRATEGY.sequencing_planning;
        setRecommendPlanConfigurationValue(this.props.line_group_uuid, strategy, "recommend_strategy", strategy);
        this.resetConfiguration(strategy);
        this.setState(
            {
                recommend_plan_parameters: getRecommendPlanConfiguration(this.props.line_group_uuid, strategy),
            },
            () => {
                this.showConfigurationModal();
                if (autorun) {
                    this.sequencingPlanning();
                }
            }
        );
    };

    showRectifyModal = (config?: t.ISchedulingConfiguration, autorun?: boolean) => {
        if (config) {
            this.setStateFromConfiguration(config, () => {
                this.showConfigurationModal();
                if (autorun) {
                    this.rectifyPlan();
                }
            });

            return;
        }

        const strategy = PLANNING_SCHEDULE_STRATEGY.rectify;
        setRecommendPlanConfigurationValue(this.props.line_group_uuid, strategy, "recommend_strategy", strategy);
        this.resetConfiguration(strategy);
        this.setState(
            {
                recommend_plan_parameters: getRecommendPlanConfiguration(this.props.line_group_uuid, strategy),
            },
            () => {
                this.showConfigurationModal();
                if (autorun) {
                    this.rectifyPlan();
                }
            }
        );
    };

    handleCriteriaChange = (criteria: keyof RecommendPlanParamsAndCriteria, selected_value: { value: number }) => {
        const strategy = this.state.recommend_plan_parameters.recommend_strategy;
        setRecommendPlanConfigurationValue(this.props.line_group_uuid, strategy, criteria, selected_value.value);
        this.resetConfiguration();
        this.setState({
            recommend_plan_parameters: getRecommendPlanConfiguration(this.props.line_group_uuid, strategy),
        });
    };

    handleMetaOptimizationChange = (selected_value: { value: PlanningMetaOptimizationEnum }) => {
        const strategy = this.state.recommend_plan_parameters.recommend_strategy;
        const meta_optimization = selected_value.value;
        setRecommendPlanConfigurationValue(
            this.props.line_group_uuid,
            strategy,
            "recommend_param_meta_optimization",
            meta_optimization
        );

        const recommend_plan_parameters = getRecommendPlanConfiguration(this.props.line_group_uuid, strategy);
        const override_shifts = recommend_plan_parameters.recommend_param_override_shifts;

        setRecommendPlanConfigurationValue(
            this.props.line_group_uuid,
            strategy,
            "recommend_param_override_shifts",
            override_shifts
        );
        this.resetConfiguration();
        this.setState({ recommend_plan_parameters });
    };

    handleRecommendParamToggle = (event: React.ChangeEvent<HTMLInputElement>) => {
        const strategy = this.state.recommend_plan_parameters.recommend_strategy;
        let target = event.target;
        if (target instanceof HTMLInputElement) {
            switch (target.id) {
                case "recommend_param_consider_alternative_lines": {
                    const recommend_param_consider_alternative_lines = !getRecommendPlanConfigurationValue(
                        this.props.line_group_uuid,
                        strategy,
                        "recommend_param_consider_alternative_lines"
                    );
                    setRecommendPlanConfigurationValue(
                        this.props.line_group_uuid,
                        strategy,
                        "recommend_param_consider_alternative_lines",
                        recommend_param_consider_alternative_lines
                    );
                    break;
                }
                case "recommend_param_consider_priority": {
                    const recommend_param_consider_priority = !getRecommendPlanConfigurationValue(
                        this.props.line_group_uuid,
                        strategy,
                        "recommend_param_consider_priority"
                    );
                    setRecommendPlanConfigurationValue(
                        this.props.line_group_uuid,
                        strategy,
                        "recommend_param_consider_priority",
                        recommend_param_consider_priority
                    );
                    break;
                }
                case "recommend_param_consider_people_presences": {
                    const recommend_param_consider_people_presences = !getRecommendPlanConfigurationValue(
                        this.props.line_group_uuid,
                        strategy,
                        "recommend_param_consider_people_presences"
                    );
                    setRecommendPlanConfigurationValue(
                        this.props.line_group_uuid,
                        strategy,
                        "recommend_param_consider_people_presences",
                        recommend_param_consider_people_presences
                    );
                    // can't consider competence without presence (turn off presence will turn off competence as well)
                    if (!recommend_param_consider_people_presences) {
                        setRecommendPlanConfigurationValue(
                            this.props.line_group_uuid,
                            strategy,
                            "recommend_param_consider_people_competences",
                            recommend_param_consider_people_presences
                        );
                    }
                    break;
                }
                case "recommend_param_consider_people_competences": {
                    const recommend_param_consider_people_competences = !getRecommendPlanConfigurationValue(
                        this.props.line_group_uuid,
                        strategy,
                        "recommend_param_consider_people_competences"
                    );
                    setRecommendPlanConfigurationValue(
                        this.props.line_group_uuid,
                        strategy,
                        "recommend_param_consider_people_competences",
                        recommend_param_consider_people_competences
                    );
                    // can't consider competence without presence (turn on competence will turn on presence as well)
                    if (recommend_param_consider_people_competences) {
                        setRecommendPlanConfigurationValue(
                            this.props.line_group_uuid,
                            strategy,
                            "recommend_param_consider_people_presences",
                            recommend_param_consider_people_competences
                        );
                    }
                    break;
                }
                case "recommend_param_track_input_material": {
                    const recommend_param_track_input_material = !getRecommendPlanConfigurationValue(
                        this.props.line_group_uuid,
                        strategy,
                        "recommend_param_track_input_material"
                    );
                    setRecommendPlanConfigurationValue(
                        this.props.line_group_uuid,
                        strategy,
                        "recommend_param_track_input_material",
                        recommend_param_track_input_material
                    );
                    break;
                }
                case "recommend_param_use_productions_of_unscheduled_operations": {
                    const recommend_param_use_productions_of_unscheduled_operations =
                        !getRecommendPlanConfigurationValue(
                            this.props.line_group_uuid,
                            strategy,
                            "recommend_param_use_productions_of_unscheduled_operations"
                        );
                    setRecommendPlanConfigurationValue(
                        this.props.line_group_uuid,
                        strategy,
                        "recommend_param_use_productions_of_unscheduled_operations",
                        recommend_param_use_productions_of_unscheduled_operations
                    );
                    break;
                }
                case "recommend_param_use_requirements_from_unscheduled_operations": {
                    const recommend_param_use_requirements_from_unscheduled_operations =
                        !getRecommendPlanConfigurationValue(
                            this.props.line_group_uuid,
                            strategy,
                            "recommend_param_use_requirements_from_unscheduled_operations"
                        );
                    setRecommendPlanConfigurationValue(
                        this.props.line_group_uuid,
                        strategy,
                        "recommend_param_use_requirements_from_unscheduled_operations",
                        recommend_param_use_requirements_from_unscheduled_operations
                    );
                    break;
                }
                case "recommend_param_use_safety_stock": {
                    const recommend_param_use_safety_stock = !getRecommendPlanConfigurationValue(
                        this.props.line_group_uuid,
                        strategy,
                        "recommend_param_use_safety_stock"
                    );
                    setRecommendPlanConfigurationValue(
                        this.props.line_group_uuid,
                        strategy,
                        "recommend_param_use_safety_stock",
                        recommend_param_use_safety_stock
                    );
                    break;
                }
                case "recommend_param_customer_priority": {
                    const recommend_param_customer_priority = !getRecommendPlanConfigurationValue(
                        this.props.line_group_uuid,
                        strategy,
                        "recommend_param_customer_priority"
                    );
                    setRecommendPlanConfigurationValue(
                        this.props.line_group_uuid,
                        strategy,
                        "recommend_param_customer_priority",
                        recommend_param_customer_priority
                    );
                    break;
                }
                case "recommend_param_include_plan_orders": {
                    const recommend_param_include_plan_orders = !getRecommendPlanConfigurationValue(
                        this.props.line_group_uuid,
                        strategy,
                        "recommend_param_include_plan_orders"
                    );
                    setRecommendPlanConfigurationValue(
                        this.props.line_group_uuid,
                        strategy,
                        "recommend_param_include_plan_orders",
                        recommend_param_include_plan_orders
                    );
                    break;
                }
                case "recommend_param_include_unscheduled_orders": {
                    const recommend_param_include_unscheduled_orders = !getRecommendPlanConfigurationValue(
                        this.props.line_group_uuid,
                        strategy,
                        "recommend_param_include_unscheduled_orders"
                    );
                    setRecommendPlanConfigurationValue(
                        this.props.line_group_uuid,
                        strategy,
                        "recommend_param_include_unscheduled_orders",
                        recommend_param_include_unscheduled_orders
                    );
                    break;
                }
                case "recommend_param_freeze_work_orders": {
                    const recommend_param_freeze_work_orders = !getRecommendPlanConfigurationValue(
                        this.props.line_group_uuid,
                        strategy,
                        "recommend_param_freeze_work_orders"
                    );
                    setRecommendPlanConfigurationValue(
                        this.props.line_group_uuid,
                        strategy,
                        "recommend_param_freeze_work_orders",
                        recommend_param_freeze_work_orders
                    );
                    break;
                }
                case "recommend_param_plan_after_frozen_orders": {
                    const recommend_param_plan_after_frozen_orders = !getRecommendPlanConfigurationValue(
                        this.props.line_group_uuid,
                        strategy,
                        "recommend_param_plan_after_frozen_orders"
                    );
                    setRecommendPlanConfigurationValue(
                        this.props.line_group_uuid,
                        strategy,
                        "recommend_param_plan_after_frozen_orders",
                        recommend_param_plan_after_frozen_orders
                    );
                    break;
                }
                case "recommend_param_include_history": {
                    const recommend_param_include_history = !getRecommendPlanConfigurationValue(
                        this.props.line_group_uuid,
                        strategy,
                        "recommend_param_include_history"
                    );
                    setRecommendPlanConfigurationValue(
                        this.props.line_group_uuid,
                        strategy,
                        "recommend_param_include_history",
                        recommend_param_include_history
                    );
                    break;
                }
                case "recommend_param_consider_optimum_batch": {
                    const recommend_param_consider_optimum_batch = !getRecommendPlanConfigurationValue(
                        this.props.line_group_uuid,
                        strategy,
                        "recommend_param_consider_optimum_batch"
                    );
                    setRecommendPlanConfigurationValue(
                        this.props.line_group_uuid,
                        strategy,
                        "recommend_param_consider_optimum_batch",
                        recommend_param_consider_optimum_batch
                    );
                    break;
                }
                case "recommend_param_can_change_workorder_line": {
                    const recommend_param_can_change_workorder_line = !getRecommendPlanConfigurationValue(
                        this.props.line_group_uuid,
                        strategy,
                        "recommend_param_can_change_workorder_line"
                    );
                    setRecommendPlanConfigurationValue(
                        this.props.line_group_uuid,
                        strategy,
                        "recommend_param_can_change_workorder_line",
                        recommend_param_can_change_workorder_line
                    );
                    break;
                }
                default:
                    console.log("unknown toggle " + target.id);
            }

            this.resetConfiguration();
            this.setState({
                recommend_plan_parameters: getRecommendPlanConfiguration(this.props.line_group_uuid, strategy),
            });
        }
    };

    handleParamInput = (
        param_name: keyof RecommendPlanParamsAndCriteria,
        min_val: number,
        event: React.ChangeEvent<HTMLSelectElement | HTMLInputElement>,
        max_value?: number
    ) => {
        const target = event.target;
        // check if we can parse
        const value = parseInt(target.value, 10);
        if (!isNumber(value)) {
            return;
        }
        // check if non-negative
        if (value < min_val) {
            return;
        }
        if (max_value !== undefined && value > max_value) {
            return;
        }
        // all fine!
        const strategy = this.state.recommend_plan_parameters.recommend_strategy;
        setRecommendPlanConfigurationValue(this.props.line_group_uuid, strategy, param_name, value);
        this.resetConfiguration();
        this.setState({
            recommend_plan_parameters: getRecommendPlanConfiguration(this.props.line_group_uuid, strategy),
        });
    };

    // function for checking if we have an overlap with existing orders
    is_overlap = (
        freeze_orders: t.IRunLinegroupPlanningFreezeOrder[],
        line_uuid: string,
        _start_time: number,
        _end_time: number,
        is_overlapping_orders_line: boolean
    ) => {
        // we ignore difference within one minute
        const timestamp_epsilon = 60 * 1000;
        let start_time = _start_time;
        let end_time = _end_time;

        if (!is_overlapping_orders_line) {
            for (const freeze_order of freeze_orders) {
                // check if same line
                if (freeze_order.line_uuid !== line_uuid) {
                    continue;
                }

                // if order subsumes the frozen other, then we cannot do much since
                // orders should be significantly longer then the epsilon
                if (start_time <= freeze_order.start_time && freeze_order.end_time <= end_time) {
                    return { overlap_order_uuid: freeze_order.uuid, start_time, end_time };
                }
                // check if order starts within some other order
                if (freeze_order.start_time < start_time && start_time < freeze_order.end_time) {
                    // check if within epsilon
                    if (freeze_order.end_time - start_time < timestamp_epsilon) {
                        // we are good, just fix start time to the end
                        start_time = freeze_order.end_time;
                    } else {
                        // overlap to large
                        return { overlap_order_uuid: freeze_order.uuid, start_time, end_time };
                    }
                }
                // check if order ends within some other order
                if (freeze_order.start_time < end_time && end_time < freeze_order.end_time) {
                    // check if within epsilon
                    if (end_time - freeze_order.start_time < timestamp_epsilon) {
                        // we are good, just fix start time to the end
                        end_time = freeze_order.start_time;
                    } else {
                        // overlap to large
                        return { overlap_order_uuid: freeze_order.uuid, start_time, end_time };
                    }
                }
            }
        }

        return {
            overlap_order_uuid: undefined,
            start_time: Math.ceil(start_time / 60000.0) * 60000.0, // round up to a minute
            end_time: Math.floor(end_time / 60000.0) * 60000.0, // rond down to am inute
        };
    };

    collectOrderQuantityProduced = (): Map<string, number> => {
        const order_uuid_to_quantity_produced = new Map<string, number>();

        // make sure we have report
        if (this.props.report_loading === true) {
            console.log("Report loading");
            return order_uuid_to_quantity_produced;
        }
        if (this.props.report.report_data === null) {
            console.log("Report NULL");
            return order_uuid_to_quantity_produced;
        }

        // get report
        const report: t2.IReportModelEx = this.props.report.report_data;
        // go over all orders in report and remember their quantity produced
        const orders = report.result.orders;
        for (const line of orders) {
            for (const order of line.production) {
                order_uuid_to_quantity_produced.set(order.order_id, order.quantity_produced);
            }
        }
        // go over unscheduled orders and collect produced numbers for them as well
        if (report.result.unscheduled) {
            for (const order of report.result.unscheduled) {
                order_uuid_to_quantity_produced.set(order.order_uuid, order.quantity_produced);
            }
        }

        return order_uuid_to_quantity_produced;
    };

    prepareFreezeOrders = (): t.IRunLinegroupPlanningFreezeOrder[] => {
        const recommend_plan_parameters = this.state.recommend_plan_parameters;

        // make sure we have report
        if (this.props.report_loading === true) {
            console.log("Report loading");
            return [];
        }
        if (this.props.report.report_data === null) {
            console.log("Report NULL");
            return [];
        }
        const report: t2.IReportModelEx = this.props.report.report_data;
        // time shortcuts
        const next_shift_date = dateFromWeekAndShift(
            report.result.next_shift_time.week,
            report.result.next_shift_time.year,
            report.result.next_shift_time.shift_number
        );
        const max_hours_start =
            recommend_plan_parameters.recommend_param_freeze_shifts * SHIFT_CONSTS.SHIFT_DURATION_HOURS;
        // first explicitly collect orders based on tags
        const freeze_orders: t.IRunLinegroupPlanningFreezeOrder[] = [];
        const work_order_types = [ORDER_TYPE.full, ORDER_TYPE.parallel];
        const frozen_order_set: Set<string> = new Set();
        const overlapping_line_uuid_set: Set<string> = new Set();
        for (const line of this.props.lines_orders) {
            if (line.tags[LINE_TAGS.overlapping_orders_line] === "true") {
                overlapping_line_uuid_set.add(line.line_uuid);
            }
        }
        for (const line of this.props.lines_orders) {
            for (const order of line.orders) {
                if (ORDER_TAGS_ACCESS.insight_freeze_order(order.tags)) {
                    // make sure we have both values
                    if (
                        ORDER_TAGS_ACCESS.freeze_time_start(order.tags) === -1 ||
                        ORDER_TAGS_ACCESS.freeze_time_end(order.tags) === -1
                    ) {
                        console.log(`Frozen order ${order.uuid} missing required tags for start and end`);
                        if (!frozen_order_set.has(order.uuid)) {
                            freeze_orders.push({
                                end_time: 0,
                                start_time: 0,
                                uuid: order.uuid,
                                line_uuid: line.line_uuid,
                                failed_to_freeze: true,
                            });
                        }
                        continue;
                    }
                    // we have frozen order!
                    const { overlap_order_uuid, start_time, end_time } = this.is_overlap(
                        freeze_orders,
                        line.line_uuid,
                        ORDER_TAGS_ACCESS.freeze_time_start(order.tags),
                        ORDER_TAGS_ACCESS.freeze_time_end(order.tags),
                        overlapping_line_uuid_set.has(line.line_uuid)
                    );
                    // report if we have overlap (but only if line is not overlapping orders line)
                    if (overlap_order_uuid) {
                        console.log(
                            `Frozen order ${order.uuid} overlaps with existing frozen order ${overlap_order_uuid}`
                        );
                        if (!frozen_order_set.has(order.uuid)) {
                            freeze_orders.push({
                                end_time,
                                start_time,
                                uuid: order.uuid,
                                line_uuid: line.line_uuid,
                                failed_to_freeze: true,
                            });
                        }
                    } else {
                        if (!frozen_order_set.has(order.uuid)) {
                            // create order
                            freeze_orders.push({
                                end_time,
                                start_time,
                                uuid: order.uuid,
                                line_uuid: line.line_uuid,
                                failed_to_freeze: false,
                            });
                            frozen_order_set.add(order.uuid);
                        }
                    }
                }
            }
        }

        // we prepare list of lines we are editing on the planning board so we can skip rest
        // of the lines in the report (report contains also all realted lines and we do not
        // need to include orders from there for frerezing)
        const planning_board_lines_set = new Set(this.props.lines_orders.map(lo => lo.line_uuid));

        // figure out which ordes are in the freeze zones and extract ther start and end timestamps
        const orders = report.result.orders;
        for (const line of orders) {
            // skip if not part of planning board
            if (!planning_board_lines_set.has(line.line)) {
                continue;
            }
            // check if this line is selected
            if (this.props.selected_line_uuids.length > 0 && !this.props.selected_line_uuids.includes(line.line)) {
                continue;
            }
            // collect orders with productions in the first freeze_shifts shifts
            let found_one_order = false;
            for (const order of line.production) {
                // check if order falls under any freeze condition
                const order_freeze_shifts = order.estimated_start[1].val < max_hours_start;
                const order_freeze_type =
                    recommend_plan_parameters.recommend_param_freeze_work_orders === true &&
                    work_order_types.includes(order.order_type as OrderTypeEnum);
                if (order_freeze_shifts || order_freeze_type) {
                    const { overlap_order_uuid, start_time, end_time } = this.is_overlap(
                        freeze_orders,
                        line.line,
                        addHours(next_shift_date, order.estimated_start[1].val).getTime(),
                        addHours(next_shift_date, order.estimated_completion[1].val).getTime(),
                        overlapping_line_uuid_set.has(line.line)
                    );
                    if (overlap_order_uuid) {
                        console.log(
                            `Frozen order ${order.order_id} overlaps with existing frozen order ${overlap_order_uuid}`
                        );
                        if (!frozen_order_set.has(order.order_id)) {
                            freeze_orders.push({
                                end_time,
                                start_time,
                                uuid: order.order_id,
                                line_uuid: line.line,
                                failed_to_freeze: true,
                            });
                        }
                    } else {
                        if (!frozen_order_set.has(order.order_id)) {
                            // create order
                            freeze_orders.push({
                                end_time,
                                start_time,
                                uuid: order.order_id,
                                line_uuid: line.line,
                                failed_to_freeze: false,
                            });
                            frozen_order_set.add(order.order_id);
                            // mark we found at least one
                            found_one_order = true;
                        }
                    }
                }
            }
            // handle special case where no order within range but we still want to fix the ongoing orders
            if (
                recommend_plan_parameters.recommend_param_freeze_shifts >= 0 &&
                found_one_order === false &&
                line.production.length > 0 &&
                line.production[0].tool_setup === false &&
                line.production[0].quantity_produced > 0 &&
                line.production[0].estimated_start[1].val <= max_hours_start
            ) {
                const order = line.production[0];
                const { overlap_order_uuid, start_time, end_time } = this.is_overlap(
                    freeze_orders,
                    line.line,
                    addHours(next_shift_date, order.estimated_start[1].val).getTime(),
                    addHours(next_shift_date, order.estimated_completion[1].val).getTime(),
                    overlapping_line_uuid_set.has(line.line)
                );
                if (overlap_order_uuid) {
                    console.log(
                        `Frozen order ${order.order_id} overlaps with existing frozen order ${overlap_order_uuid}`
                    );
                    if (!frozen_order_set.has(order.order_id)) {
                        freeze_orders.push({
                            end_time,
                            start_time,
                            uuid: line.production[0].order_id,
                            line_uuid: line.line,
                            failed_to_freeze: true,
                        });
                    }
                } else {
                    if (!frozen_order_set.has(order.order_id)) {
                        // create order
                        freeze_orders.push({
                            end_time,
                            start_time,
                            uuid: line.production[0].order_id,
                            line_uuid: line.line,
                            failed_to_freeze: false,
                        });
                        // mark we found at least one
                        found_one_order = true;
                    }
                }
            }
        }
        return freeze_orders;
    };

    recommendPlan = () => {
        const lines_orders = this.props.lines_orders;
        const unscheduled_orders = this.props.unscheduled_orders;
        const original_lines_orders = this.props.original_lines_orders;
        const original_unscheduled_orders = this.props.original_unscheduled_orders;
        const recommend_plan_parameters = this.state.recommend_plan_parameters;
        const parameters: RunRecommendPlanParameters = {
            line_group_uuid: this.props.line_group_uuid,
            line_uuids: [...this.state.selected_lines],
            state: {
                recommend_param_beam_size: recommend_plan_parameters.recommend_param_beam_size,
                recommend_param_can_change_workorder_line:
                    recommend_plan_parameters.recommend_param_can_change_workorder_line,
                recommend_param_track_input_material: recommend_plan_parameters.recommend_param_track_input_material,
                recommend_param_use_productions_of_unscheduled_operations:
                    recommend_plan_parameters.recommend_param_use_productions_of_unscheduled_operations,
                recommend_param_use_requirements_from_unscheduled_operations:
                    recommend_plan_parameters.recommend_param_use_requirements_from_unscheduled_operations,
                recommend_param_use_safety_stock: recommend_plan_parameters.recommend_param_use_safety_stock,
                recommend_param_customer_priority: recommend_plan_parameters.recommend_param_customer_priority,
                recommend_param_include_plan_orders: recommend_plan_parameters.recommend_param_include_plan_orders,
                recommend_param_include_unscheduled_orders:
                    recommend_plan_parameters.recommend_param_include_unscheduled_orders,
                recommend_param_freeze_work_orders: recommend_plan_parameters.recommend_param_freeze_work_orders,
                recommend_param_plan_after_frozen_orders:
                    recommend_plan_parameters.recommend_param_plan_after_frozen_orders,
                recommend_param_freeze_shifts: recommend_plan_parameters.recommend_param_freeze_shifts,
                recommend_param_time_horizont_shifts: recommend_plan_parameters.recommend_param_time_horizont_shifts,
                recommend_param_unscheduled_offset_shifts:
                    recommend_plan_parameters.recommend_param_time_horizont_shifts,
                recommend_param_meta_optimization: recommend_plan_parameters.recommend_param_meta_optimization,
                recommend_param_override_shifts: recommend_plan_parameters.recommend_param_override_shifts,
                recommend_param_need_score_factor: recommend_plan_parameters.recommend_param_need_score_factor,
                recommend_param_setup_code_change_factor:
                    recommend_plan_parameters.recommend_param_setup_code_change_factor,
                recommend_param_work_duration_factor: recommend_plan_parameters.recommend_param_work_duration_factor,
                recommend_criteria_delays: recommend_plan_parameters.recommend_criteria_delays,
                recommend_criteria_tool_changes: recommend_plan_parameters.recommend_criteria_tool_changes,
                recommend_criteria_intermediate_stock: recommend_plan_parameters.recommend_criteria_intermediate_stock,
                recommend_strategy: recommend_plan_parameters.recommend_strategy,
            },
            lines_orders,
            unscheduled_orders,
            planning_weeks: Math.floor(this.props.properties.days / 7),
            freeze_orders: this.prepareFreezeOrders(),
            plan_after_frozen_orders: recommend_plan_parameters.recommend_param_plan_after_frozen_orders,
            order_quanity_produced_map: this.collectOrderQuantityProduced(),
            rearrange_orders_map: LinesOrdersLogic.rearrangeOrdersMap(
                original_lines_orders || [],
                original_unscheduled_orders || []
            ),
            original_lines_orders: original_lines_orders ?? [],
        };

        RecommendPlanLogic.load(parameters, this.props.reduxDispatch);
    };

    collectOrderTimeStart = (): Map<string, number> => {
        const order_uuid_to_time_start_map = new Map<string, number>();

        // make sure we have report
        if (this.props.report_loading === true) {
            console.log("Report loading");
            return order_uuid_to_time_start_map;
        }
        if (this.props.report.report_data === null) {
            console.log("Report NULL");
            return order_uuid_to_time_start_map;
        }

        // get report
        const report: t2.IReportModelEx = this.props.report.report_data;
        // time shortcuts
        const next_shift_date = dateFromWeekAndShift(
            report.result.next_shift_time.week,
            report.result.next_shift_time.year,
            report.result.next_shift_time.shift_number
        );
        // go over all orders in report and remember their time start
        const orders = report.result.orders;
        for (const line of orders) {
            for (const order of line.production) {
                const time_start = addHours(next_shift_date, order.estimated_start[1].val).getTime();
                order_uuid_to_time_start_map.set(order.order_id, time_start);
            }
        }

        return order_uuid_to_time_start_map;
    };

    rectifyPlan = () => {
        const lines_orders = this.props.lines_orders;
        const unscheduled_orders = this.props.unscheduled_orders;
        const original_lines_orders = this.props.original_lines_orders;
        const original_unscheduled_orders = this.props.original_unscheduled_orders;
        const recommend_plan_parameters = this.state.recommend_plan_parameters;
        const planning_weeks = BusinessLogic.getLineGroupTagInt(
            this.props.line_group_uuid,
            LINE_GROUP_TAGS.planning_table_weeks,
            4
        );

        const parameters: RunRecommendPlanParameters = {
            line_group_uuid: this.props.line_group_uuid,
            line_uuids: [...this.state.selected_lines],
            state: {
                recommend_param_beam_size: PLANNING_PARAMS_DEFAULT.beam_size,
                recommend_param_can_change_workorder_line: false,
                recommend_param_track_input_material: false,
                recommend_param_use_productions_of_unscheduled_operations: false,
                recommend_param_use_requirements_from_unscheduled_operations: false,
                recommend_param_use_safety_stock: false,
                recommend_param_customer_priority: false,
                recommend_param_include_plan_orders: recommend_plan_parameters.recommend_param_include_plan_orders,
                recommend_param_include_unscheduled_orders: false,
                recommend_param_freeze_work_orders: false,
                recommend_param_plan_after_frozen_orders: false,
                recommend_param_freeze_shifts: -1,
                recommend_param_time_horizont_shifts: planning_weeks * 21,
                recommend_param_unscheduled_offset_shifts:
                    recommend_plan_parameters.recommend_param_time_horizont_shifts,
                recommend_param_meta_optimization: PLANNING_META_OPTIMIZATION.none,
                recommend_param_override_shifts: undefined,
                recommend_param_need_score_factor: 0,
                recommend_param_setup_code_change_factor: BusinessLogic.getLineGroupTagInt(
                    this.props.line_group_uuid,
                    LINE_GROUP_TAGS.planning_setup_code_change_factor,
                    2
                ),
                recommend_param_work_duration_factor: 0,
                recommend_criteria_delays: 1,
                recommend_criteria_tool_changes: 1,
                recommend_criteria_intermediate_stock: 1,
                recommend_strategy: PLANNING_SCHEDULE_STRATEGY.rectify,
            },
            lines_orders,
            unscheduled_orders,
            planning_weeks,
            freeze_orders: [],
            plan_after_frozen_orders: false,
            order_quanity_produced_map: this.collectOrderQuantityProduced(),
            order_time_start_map: this.collectOrderTimeStart(),
            rearrange_orders_map: LinesOrdersLogic.rearrangeOrdersMap(
                original_lines_orders || [],
                original_unscheduled_orders || []
            ),
            original_lines_orders: original_lines_orders ?? [],
        };

        RecommendPlanLogic.load(parameters, this.props.reduxDispatch);
    };

    backwardPlanning = () => {
        const lines_orders = this.props.lines_orders;
        const unscheduled_orders = this.props.unscheduled_orders;
        const recommend_plan_parameters = this.state.recommend_plan_parameters;
        const planning_weeks = BusinessLogic.getLineGroupTagInt(
            this.props.line_group_uuid,
            LINE_GROUP_TAGS.planning_table_weeks,
            4
        );
        const report_data = this.props.report.report_data;

        const parameters: RunBackwardPlanParameters = {
            report_data,
            line_group_uuid: this.props.line_group_uuid,
            line_uuids: this.props.selected_line_uuids,
            state: {
                recommend_planning_table_horizon_days: recommend_plan_parameters.recommend_planning_table_horizon_days,
                recommend_param_beam_size: PLANNING_PARAMS_DEFAULT.beam_size,
                recommend_param_can_change_workorder_line: false,
                recommend_param_track_input_material: false,
                recommend_param_use_productions_of_unscheduled_operations: false,
                recommend_param_use_requirements_from_unscheduled_operations: false,
                recommend_param_use_safety_stock: false,
                recommend_param_consider_alternative_lines:
                    recommend_plan_parameters.recommend_param_consider_alternative_lines,
                recommend_param_consider_priority: recommend_plan_parameters.recommend_param_consider_priority,
                recommend_param_customer_priority: false,
                recommend_param_consider_people_presences:
                    recommend_plan_parameters.recommend_param_consider_people_presences,
                recommend_param_consider_people_competences:
                    recommend_plan_parameters.recommend_param_consider_people_competences,
                recommend_param_include_plan_orders: recommend_plan_parameters.recommend_param_include_plan_orders,
                recommend_param_include_unscheduled_orders: true,
                recommend_param_freeze_work_orders: false,
                recommend_param_freeze_shifts: -1,
                recommend_param_plan_after_frozen_orders: false,
                recommend_param_time_horizont_shifts: recommend_plan_parameters.recommend_param_time_horizont_shifts,
                recommend_param_unscheduled_offset_shifts:
                    recommend_plan_parameters.recommend_param_time_horizont_shifts,
                recommend_param_meta_optimization: PLANNING_META_OPTIMIZATION.none,
                recommend_param_override_shifts: undefined,
                recommend_param_need_score_factor: 0,
                recommend_param_setup_code_change_factor: BusinessLogic.getLineGroupTagInt(
                    this.props.line_group_uuid,
                    LINE_GROUP_TAGS.planning_setup_code_change_factor,
                    2
                ),
                recommend_param_work_duration_factor: 0,
                recommend_criteria_delays: 1,
                recommend_criteria_tool_changes: 1,
                recommend_criteria_intermediate_stock: 1,
                recommend_strategy: PLANNING_SCHEDULE_STRATEGY.backward_planning,
            },
            lines_orders,
            unscheduled_orders,
            planning_weeks,
        };

        BackwardPlanLogic.run(parameters, this.props.reduxDispatch);
    };

    sequencingPlanning = () => {
        const lines_orders = this.props.lines_orders;
        const unscheduled_orders = this.props.unscheduled_orders;
        const recommend_plan_parameters = this.state.recommend_plan_parameters;
        const planning_weeks = BusinessLogic.getLineGroupTagInt(
            this.props.line_group_uuid,
            LINE_GROUP_TAGS.planning_table_weeks,
            4
        );
        const report_data = this.props.report.report_data;

        const parameters: RunBackwardPlanParameters = {
            report_data,
            line_group_uuid: this.props.line_group_uuid,
            line_uuids: [...this.state.selected_lines],
            state: {
                recommend_planning_table_horizon_days: recommend_plan_parameters.recommend_planning_table_horizon_days,
                recommend_param_beam_size: PLANNING_PARAMS_DEFAULT.beam_size,
                recommend_param_can_change_workorder_line: false,
                recommend_param_track_input_material: false,
                recommend_param_use_productions_of_unscheduled_operations: false,
                recommend_param_use_requirements_from_unscheduled_operations: false,
                recommend_param_use_safety_stock: false,
                recommend_param_consider_alternative_lines:
                    recommend_plan_parameters.recommend_param_consider_alternative_lines,
                recommend_param_customer_priority: false,
                recommend_param_consider_people_presences:
                    recommend_plan_parameters.recommend_param_consider_people_presences,
                recommend_param_consider_people_competences:
                    recommend_plan_parameters.recommend_param_consider_people_competences,
                recommend_param_include_plan_orders: recommend_plan_parameters.recommend_param_include_plan_orders,
                recommend_param_include_unscheduled_orders: true,
                recommend_param_freeze_work_orders: false,
                recommend_param_freeze_shifts: -1,
                recommend_param_plan_after_frozen_orders: false,
                recommend_param_time_horizont_shifts: planning_weeks * 21,
                recommend_param_unscheduled_offset_shifts:
                    recommend_plan_parameters.recommend_param_time_horizont_shifts,
                recommend_param_meta_optimization: PLANNING_META_OPTIMIZATION.none,
                recommend_param_override_shifts: undefined,
                recommend_param_need_score_factor: 0,
                recommend_param_setup_code_change_factor: BusinessLogic.getLineGroupTagInt(
                    this.props.line_group_uuid,
                    LINE_GROUP_TAGS.planning_setup_code_change_factor,
                    2
                ),
                recommend_param_work_duration_factor: 0,
                recommend_param_max_order_end_date: recommend_plan_parameters.recommend_param_max_order_end_date,
                recommend_param_horizon_limit: recommend_plan_parameters.recommend_param_horizon_limit,
                recommend_param_alternatives_strategy: recommend_plan_parameters.recommend_param_alternatives_strategy,
                recommend_param_consider_optimum_batch:
                    recommend_plan_parameters.recommend_param_consider_optimum_batch,
                recommend_param_run_until_phase: recommend_plan_parameters.recommend_param_run_until_phase,
                recommend_param_include_history: recommend_plan_parameters.recommend_param_include_history,
                recommend_criteria_delays: 1,
                recommend_criteria_tool_changes: 1,
                recommend_criteria_intermediate_stock: 1,
                recommend_strategy: PLANNING_SCHEDULE_STRATEGY.sequencing_planning,
            },
            lines_orders,
            unscheduled_orders,
            planning_weeks,
        };
        SequencingPlanLogic.run(parameters, this.props.reduxDispatch);
    };

    getIsDisabled = () => {
        return !BusinessLogic.getLineGroupTagBool(
            this.props.line_group_uuid,
            LINE_GROUP_TAGS.automatic_scheduling,
            false
        );
    };

    getBackwardPlaningIsEnabled = () => {
        const line_group_tags = BusinessLogic.getLineGroupTags(this.props.line_group_uuid);
        return LINE_GROUP_TAGS_ACCESS.backward_scheduling(line_group_tags);
    };

    onSetDefault = () => {
        const strategy = this.state.recommend_plan_parameters.recommend_strategy;
        const line_group_uuid = this.props.line_group_uuid;
        const initial_state = getRecommendPlanInitialConfiguration(line_group_uuid);
        if (this.state.recommend_plan_parameters.recommend_strategy === PLANNING_SCHEDULE_STRATEGY.rectify) {
            initial_state.recommend_strategy = PLANNING_SCHEDULE_STRATEGY.rectify;
        }
        if (this.state.recommend_plan_parameters.recommend_strategy === PLANNING_SCHEDULE_STRATEGY.backward_planning) {
            initial_state.recommend_strategy = PLANNING_SCHEDULE_STRATEGY.backward_planning;
        }

        setRecommendPlanConfiguration(line_group_uuid, strategy, initial_state);
        this.resetConfiguration(initial_state.recommend_strategy);
        this.setState({
            recommend_plan_parameters: initial_state,
        });
    };

    getPlanHorizontMsg = () => {
        const automatic_scheduling_unit = BusinessLogic.getLineGroupTagStr(
            this.props.line_group_uuid,
            LINE_GROUP_TAGS.planning_time_horizont_unit,
            PLAN_HORIZON_UNIT_AUTOMATIC_SCHEDULING.week_unit
        );
        let unit_msg = translate("common.shift", "Shift");
        if (automatic_scheduling_unit === PLAN_HORIZON_UNIT_AUTOMATIC_SCHEDULING.week_unit) {
            unit_msg = translate("common.week", "Week");
        }
        return (
            translate("Manufacturing.Planning.param_plan_horizont_without_unit", "Plan horizont") +
            " (" +
            unit_msg +
            ")"
        );
    };

    mapWarnings = (
        warnings: t.IPlanWarning[]
    ): {
        external_id: string;
        proces_num: string;
        description: React.ReactNode;
        explanation: React.ReactNode;
        Details?: (props: Pick<WarningDetailsProps, "container_ref">) => React.JSX.Element;
    }[] => {
        const warning_descriptions = {
            frozen: translate(
                "Manufacturing.Planning.warning_frozen",
                "Operation was frozen but logic for placing it on predefined place failed"
            ),
            rectify_unscheduled: translate(
                "Manufacturing.Planning.warning_rectify_unscheduled",
                "Rectify logic could not place operation on the schedule"
            ),
            prod_ver_mismatch: translate(
                "Manufacturing.Planning.warning_prod_ver_mismatch",
                "Available production versions for scheduling the operations do not match matrix constraints"
            ),
        };
        const order_map: Map<string, IOrderProducedModelEx> = new Map();

        for (const line of this.props.lines_orders) {
            for (const order of line.orders) {
                order_map.set(order.uuid, order);
            }
        }

        for (const line of this.props.unscheduled_orders) {
            for (const order of line.orders) {
                order_map.set(order.uuid, order);
            }
        }

        return warnings.map(w => {
            const order = order_map.get(w.operation_uuid);
            const explanation =
                w.type === "rectify_unscheduled" ? RecommendPlanLogic.getExplanationText(w.explanation) : null;
            let Details;
            if (w.explanation.extra_data.split_operation) {
                Details = () => <SplitOperationWarningDetails container_ref={this.modal_ref} warning={w} />;
            }

            return {
                external_id: order !== undefined ? order.external_id : "",
                proces_num: order !== undefined ? order.process_num : "",
                description: warning_descriptions[w.type],
                explanation: explanation !== null ? `${explanation}.` : "",
                Details,
            };
        });
    };

    loadConfigurations = async () => {
        const { configs } = await getBackend().manufacturing.getSchedulingConfigurations({
            line_group_uuid: this.props.line_group_uuid,
        });
        this.setState({
            configurations: configs.sort((a, b) => a.name.localeCompare(b.name)),
        });
    };

    updateConfiguration = async (config: t.ISchedulingConfiguration) => {
        await getBackend().manufacturing.updateSchedulingConfiguration({ config });
    };

    deleteConfiguration = async (name: string) => {
        await getBackend().manufacturing.deleteSchedulingConfiguration({
            line_group_uuid: this.props.line_group_uuid,
            strategy: this.state.recommend_plan_parameters.recommend_strategy,
            name,
        });
    };

    getConfiguration = (name: string, strategy?: string): t.ISchedulingConfiguration | undefined => {
        const scheduling_strategy = strategy || this.state.recommend_plan_parameters.recommend_strategy;
        return this.state.configurations.find(
            config => config.name === name && config.strategy === scheduling_strategy
        );
    };

    setConfiguration = (name: string, strategy?: string) => {
        const scheduling_strategy = strategy || this.state.recommend_plan_parameters.recommend_strategy;
        const config = this.getConfiguration(name, scheduling_strategy);
        if (config !== undefined) {
            this.setStateFromConfiguration(config);
        } else {
            this.resetConfiguration(scheduling_strategy);
        }
    };

    resetConfiguration = (strategy?: string) => {
        const scheduling_strategy = strategy || this.state.recommend_plan_parameters.recommend_strategy;
        this.setState({
            configuration: "",
        });
        setConfigurationName(scheduling_strategy, "");
    };

    getConfigurationsOptions = () => {
        return [
            {
                label: translate("Manufacturing.Planning.custom_configuration", "Custom configuration"),
                value: "",
            },
            ...this.state.configurations
                .filter(config =>
                    this.state.recommend_plan_parameters.recommend_strategy === PLANNING_SCHEDULE_STRATEGY.rectify
                        ? config.strategy === PLANNING_SCHEDULE_STRATEGY.rectify
                        : this.state.recommend_plan_parameters.recommend_strategy ===
                            PLANNING_SCHEDULE_STRATEGY.backward_planning
                          ? config.strategy === PLANNING_SCHEDULE_STRATEGY.backward_planning
                          : this.state.recommend_plan_parameters.recommend_strategy ===
                              PLANNING_SCHEDULE_STRATEGY.sequencing_planning
                            ? config.strategy === PLANNING_SCHEDULE_STRATEGY.sequencing_planning
                            : config.strategy !== PLANNING_SCHEDULE_STRATEGY.sequencing_planning
                )
                .map(config => ({
                    label: config.name,
                    value: config.name,
                })),
        ];
    };

    getSelectedConfigurationOption = () => {
        const options = this.getConfigurationsOptions();
        return options.find(config => config.value === this.state.configuration) || options[0];
    };

    hasUniqueName = (name: string) => {
        return (
            this.state.configurations.find(
                config =>
                    config.strategy === this.state.recommend_plan_parameters.recommend_strategy && config.name === name
            ) !== undefined
        );
    };

    getConfigurationFromState = (name: string): t.ISchedulingConfiguration => {
        const recommend_plan_parameters = this.state.recommend_plan_parameters;
        const config: t.ISchedulingConfiguration = {
            name,
            line_group_uuid: this.props.line_group_uuid,
            strategy: recommend_plan_parameters.recommend_strategy,
            params: {
                include_plan_orders: recommend_plan_parameters.recommend_param_include_plan_orders,
                time_horizont_shifts: recommend_plan_parameters.recommend_param_time_horizont_shifts,
                unscheduled_offset_shifts: recommend_plan_parameters.recommend_param_unscheduled_offset_shifts,
            },
            criteria: {},
        };

        if (recommend_plan_parameters.recommend_strategy === PLANNING_SCHEDULE_STRATEGY.BFS) {
            config.params.beam_size = recommend_plan_parameters.recommend_param_beam_size;
            config.params.can_change_workorder_line =
                recommend_plan_parameters.recommend_param_can_change_workorder_line;
            config.params.customer_priority = recommend_plan_parameters.recommend_param_customer_priority;
            config.params.freeze_shifts = recommend_plan_parameters.recommend_param_freeze_shifts;
            config.params.freeze_work_orders = recommend_plan_parameters.recommend_param_freeze_work_orders;
            config.params.plan_after_frozen_orders = recommend_plan_parameters.recommend_param_plan_after_frozen_orders;
            config.params.include_unscheduled_orders =
                recommend_plan_parameters.recommend_param_include_unscheduled_orders;
            config.params.meta_optimization = recommend_plan_parameters.recommend_param_meta_optimization;
            config.params.override_shifts = recommend_plan_parameters.recommend_param_override_shifts;
            config.params.need_score_factor = recommend_plan_parameters.recommend_param_need_score_factor;
            config.params.setup_code_change_factor = recommend_plan_parameters.recommend_param_setup_code_change_factor;
            config.params.track_input_material = recommend_plan_parameters.recommend_param_track_input_material;
            config.params.use_productions_of_unscheduled_operations =
                recommend_plan_parameters.recommend_param_use_productions_of_unscheduled_operations;
            config.params.use_requirements_from_unscheduled_operations =
                recommend_plan_parameters.recommend_param_use_requirements_from_unscheduled_operations;
            config.params.use_safety_stock = recommend_plan_parameters.recommend_param_use_safety_stock;
            config.params.work_duration_factor = recommend_plan_parameters.recommend_param_work_duration_factor;
            config.criteria.delays = recommend_plan_parameters.recommend_criteria_delays;
            config.criteria.intermediate_stock = recommend_plan_parameters.recommend_criteria_intermediate_stock;
            config.criteria.tool_changes = recommend_plan_parameters.recommend_criteria_tool_changes;
        }

        if (recommend_plan_parameters.recommend_strategy === PLANNING_SCHEDULE_STRATEGY.backward_planning) {
            config.params.consider_alternative_lines =
                recommend_plan_parameters.recommend_param_consider_alternative_lines;
            config.params.consider_priority = recommend_plan_parameters.recommend_param_consider_priority;
            config.params.consider_people_presences =
                recommend_plan_parameters.recommend_param_consider_people_presences;
            config.params.consider_people_competences =
                recommend_plan_parameters.recommend_param_consider_people_competences;
        }

        if (recommend_plan_parameters.recommend_strategy === PLANNING_SCHEDULE_STRATEGY.sequencing_planning) {
            config.params.consider_alternative_lines =
                recommend_plan_parameters.recommend_param_consider_alternative_lines;
            config.params.max_order_end_date = recommend_plan_parameters.recommend_param_max_order_end_date;
            config.params.horizon_limit = recommend_plan_parameters.recommend_param_horizon_limit;
            config.params.alternatives_strategy = recommend_plan_parameters.recommend_param_alternatives_strategy;
            config.params.consider_optimum_batch = recommend_plan_parameters.recommend_param_consider_optimum_batch;
            config.params.run_until_phase = recommend_plan_parameters.recommend_param_run_until_phase;
            config.params.include_history = recommend_plan_parameters.recommend_param_include_history;
        }

        return config;
    };

    getLinesOptions = () =>
        this.props.lines_orders.map(line_orders => ({
            label: line_orders.line_title,
            value: line_orders.line_uuid,
        }));

    getSelectedLinesOptions = () =>
        this.props.lines_orders
            .filter(line_orders => this.state.selected_lines.has(line_orders.line_uuid))
            .map(line_orders => ({
                label: line_orders.line_title,
                value: line_orders.line_uuid,
            }));

    setStateFromConfiguration = (config: t.ISchedulingConfiguration, callback = () => {}) => {
        const recommend_plan_parameters = getRecommendPlanConfiguration(this.props.line_group_uuid, config.strategy);
        recommend_plan_parameters.recommend_strategy = config.strategy;
        recommend_plan_parameters.recommend_param_include_plan_orders = config.params.include_plan_orders;
        recommend_plan_parameters.recommend_param_time_horizont_shifts = config.params.time_horizont_shifts;
        recommend_plan_parameters.recommend_param_unscheduled_offset_shifts = config.params.unscheduled_offset_shifts;

        if (config.params.planning_table_horizon_days !== undefined) {
            recommend_plan_parameters.recommend_planning_table_horizon_days = config.params.planning_table_horizon_days;
        }

        if (config.strategy === PLANNING_SCHEDULE_STRATEGY.BFS) {
            if (config.params.beam_size !== undefined) {
                recommend_plan_parameters.recommend_param_beam_size = config.params.beam_size;
            }

            if (config.params.can_change_workorder_line !== undefined) {
                recommend_plan_parameters.recommend_param_can_change_workorder_line =
                    config.params.can_change_workorder_line;
            }

            if (config.params.customer_priority !== undefined) {
                recommend_plan_parameters.recommend_param_customer_priority = config.params.customer_priority;
            }

            if (config.params.freeze_shifts !== undefined) {
                recommend_plan_parameters.recommend_param_freeze_shifts = config.params.freeze_shifts;
            }

            if (config.params.freeze_work_orders !== undefined) {
                recommend_plan_parameters.recommend_param_freeze_work_orders = config.params.freeze_work_orders;
            }

            if (config.params.plan_after_frozen_orders !== undefined) {
                recommend_plan_parameters.recommend_param_plan_after_frozen_orders =
                    config.params.plan_after_frozen_orders;
            }

            if (config.params.include_unscheduled_orders !== undefined) {
                recommend_plan_parameters.recommend_param_include_unscheduled_orders =
                    config.params.include_unscheduled_orders;
            }

            if (config.params.meta_optimization !== undefined) {
                recommend_plan_parameters.recommend_param_meta_optimization = config.params.meta_optimization;
            }

            if (config.params.override_shifts !== undefined) {
                recommend_plan_parameters.recommend_param_override_shifts = config.params.override_shifts;
            }

            if (config.params.need_score_factor !== undefined) {
                recommend_plan_parameters.recommend_param_need_score_factor = config.params.need_score_factor;
            }

            if (config.params.setup_code_change_factor !== undefined) {
                recommend_plan_parameters.recommend_param_setup_code_change_factor =
                    config.params.setup_code_change_factor;
            }

            if (config.params.track_input_material !== undefined) {
                recommend_plan_parameters.recommend_param_track_input_material = config.params.track_input_material;
            }

            if (config.params.use_productions_of_unscheduled_operations !== undefined) {
                recommend_plan_parameters.recommend_param_use_productions_of_unscheduled_operations =
                    config.params.use_productions_of_unscheduled_operations;
            }

            if (config.params.use_requirements_from_unscheduled_operations !== undefined) {
                recommend_plan_parameters.recommend_param_use_requirements_from_unscheduled_operations =
                    config.params.use_requirements_from_unscheduled_operations;
            }

            if (config.params.use_safety_stock !== undefined) {
                recommend_plan_parameters.recommend_param_use_safety_stock = config.params.use_safety_stock;
            }

            if (config.params.work_duration_factor !== undefined) {
                recommend_plan_parameters.recommend_param_work_duration_factor = config.params.work_duration_factor;
            }

            if (config.criteria.delays !== undefined) {
                recommend_plan_parameters.recommend_criteria_delays = config.criteria.delays;
            }

            if (config.criteria.intermediate_stock !== undefined) {
                recommend_plan_parameters.recommend_criteria_intermediate_stock = config.criteria.intermediate_stock;
            }

            if (config.criteria.tool_changes !== undefined) {
                recommend_plan_parameters.recommend_criteria_tool_changes = config.criteria.tool_changes;
            }
        }

        if (config.strategy === PLANNING_SCHEDULE_STRATEGY.backward_planning) {
            if (config.params.consider_alternative_lines !== undefined) {
                recommend_plan_parameters.recommend_param_consider_alternative_lines =
                    config.params.consider_alternative_lines;
            }

            if (config.params.consider_priority !== undefined) {
                recommend_plan_parameters.recommend_param_consider_priority = config.params.consider_priority;
            }

            if (config.params.consider_people_presences !== undefined) {
                recommend_plan_parameters.recommend_param_consider_people_presences =
                    config.params.consider_people_presences;
            }

            if (config.params.consider_people_competences !== undefined) {
                recommend_plan_parameters.recommend_param_consider_people_competences =
                    config.params.consider_people_competences;
            }
        }

        if (config.strategy === PLANNING_SCHEDULE_STRATEGY.sequencing_planning) {
            if (config.params.consider_alternative_lines !== undefined) {
                recommend_plan_parameters.recommend_param_consider_alternative_lines =
                    config.params.consider_alternative_lines;
            }

            if (config.params.max_order_end_date !== undefined) {
                recommend_plan_parameters.recommend_param_max_order_end_date = config.params.max_order_end_date;
            }

            if (config.params.horizon_limit !== undefined) {
                recommend_plan_parameters.recommend_param_horizon_limit = config.params.horizon_limit;
            }
        }

        setRecommendPlanConfiguration(this.props.line_group_uuid, config.strategy, recommend_plan_parameters);
        setConfigurationName(config.strategy, config.name);
        this.setState(
            {
                configuration: config.name,
                recommend_plan_parameters: getRecommendPlanConfiguration(this.props.line_group_uuid, config.strategy),
            },
            callback
        );
    };

    hasWarnings = () => {
        return this.props.warnings.length > 0 || LinesOrdersLogic.hasUnsavedChunks();
    };

    handleConfigurationChange = (option: ILabelValuePair<string>) => {
        this.setConfiguration(option.value);
    };

    handleConfigurationSave = () => {
        this.setState({
            show_file_name_modal: true,
        });
    };

    handleConfigurationSaveConfirm = async (name: string) => {
        this.setState({
            show_file_name_modal: false,
        });
        await this.updateConfiguration(this.getConfigurationFromState(name));
        await this.loadConfigurations();
        this.setConfiguration(name);
    };

    handleConfigurationSaveCancel = () => {
        this.setState({
            show_file_name_modal: false,
        });
    };

    handleConfigurationDelete = () => {
        this.setState({
            show_delete_modal: true,
        });
    };

    handleConfigurationDeleteConfirm = async () => {
        this.setState({
            show_delete_modal: false,
        });
        await this.deleteConfiguration(this.state.configuration);
        await this.loadConfigurations();
        this.resetConfiguration();
    };

    handleConfigurationDeleteCancel = () => {
        this.setState({
            show_delete_modal: false,
        });
    };

    handleTabSelect = (selected_tab: string | null) => {
        if (selected_tab !== null) {
            this.setState({
                selected_tab,
            });
        }
    };

    handleBackwardPlanningSelect = async () => {
        this.showBackwardPlanningModal();
    };

    handleSequencingPlanningSelect = async () => {
        this.showSequencingPlanningModal();
    };

    handleLineSelect = (options: ILabelValuePair<string>[]) => {
        this.setState({
            selected_lines: new Set(options.map(option => option.value)),
        });
    };

    handlePlanningRun = () => {
        const strategy = this.state.recommend_plan_parameters.recommend_strategy;
        switch (strategy) {
            case PLANNING_SCHEDULE_STRATEGY.BFS:
                return this.recommendPlan();
            case PLANNING_SCHEDULE_STRATEGY.rectify:
                return this.rectifyPlan();
            case PLANNING_SCHEDULE_STRATEGY.backward_planning:
                return this.backwardPlanning();
            case PLANNING_SCHEDULE_STRATEGY.sequencing_planning:
                return this.sequencingPlanning();
        }
    };

    renderDropdown = () => {
        const recommend_configs = this.state.configurations.filter(
            config => config.strategy === PLANNING_SCHEDULE_STRATEGY.BFS
        );
        const rectify_configs = this.state.configurations.filter(
            config => config.strategy === PLANNING_SCHEDULE_STRATEGY.rectify
        );
        const backward_planning_configs = this.state.configurations.filter(
            config => config.strategy === PLANNING_SCHEDULE_STRATEGY.backward_planning
        );
        const sequencing_planning_configs = this.state.configurations.filter(
            config => config.strategy === PLANNING_SCHEDULE_STRATEGY.sequencing_planning
        );
        const is_agrana = Auth.getOrgData().code == "agrana";
        const is_demo = Auth.getOrgData().code.startsWith("qindustry");
        const env_flag = BusinessLogic.getSysFlag("env");
        const is_demo_nondev = is_demo && !env_flag.includes("dev");

        return (
            <div className="recommend-plan__dropdown">
                <Dropdown>
                    <Dropdown.Toggle
                        as="button"
                        className={
                            "btn btn-icon btn-icon-magic " +
                            (this.getRecommendIsRunning() ? "btn-danger" : "btn-primary")
                        }
                        disabled={this.getIsDisabled()}
                        aria-label={translate("common.schedule", "Schedule")}
                    >
                        <span className="sr-only">{translate("common.schedule", "Schedule")}</span>
                        {!this.getIsDisabled() && <i className="fas fa-chevron-down" />}
                    </Dropdown.Toggle>
                    <Dropdown.Menu align="right">
                        {!is_agrana && (
                            <Dropdown.Item
                                onClick={() => {
                                    const strategy = PLANNING_SCHEDULE_STRATEGY.BFS;
                                    this.showRecommendModal(
                                        this.getConfiguration(getConfigurationName(strategy), strategy)
                                    );
                                }}
                            >
                                {translate("Manufacturing.Planning.recommend", "Recommend")}...
                            </Dropdown.Item>
                        )}
                        {!is_agrana && !is_demo_nondev && (
                            <Dropdown.Item
                                onClick={() => {
                                    const strategy = PLANNING_SCHEDULE_STRATEGY.rectify;
                                    this.showRectifyModal(
                                        this.getConfiguration(getConfigurationName(strategy), strategy)
                                    );
                                }}
                            >
                                {translate("Manufacturing.Planning.rectify", "Rectify")}...
                            </Dropdown.Item>
                        )}
                        {this.getBackwardPlaningIsEnabled() && !is_agrana && !is_demo_nondev && (
                            <Dropdown.Item onClick={this.handleBackwardPlanningSelect}>
                                {translate("Manufacturing.Planning.backward_planning", "Backward planning")}...
                            </Dropdown.Item>
                        )}
                        {(is_agrana || is_demo) && (
                            <Dropdown.Item onClick={this.handleSequencingPlanningSelect}>
                                {translate("Manufacturing.Planning.sequencing_scheduling", "Planning-order scheduling")}
                                ...
                            </Dropdown.Item>
                        )}
                        {!is_agrana && recommend_configs.length > 0 && (
                            <>
                                <Dropdown.Divider />
                                <Dropdown.Header>
                                    {translate("Manufacturing.Planning.recommend", "Recommend")}
                                </Dropdown.Header>
                                {recommend_configs.map(config => (
                                    <Dropdown.Item
                                        key={`${config.strategy}-${config.name}`}
                                        onClick={() => {
                                            this.showRecommendModal(config, true);
                                        }}
                                    >
                                        {config.name}
                                    </Dropdown.Item>
                                ))}
                            </>
                        )}
                        {!is_agrana && rectify_configs.length > 0 && (
                            <>
                                <Dropdown.Divider />
                                <Dropdown.Header>
                                    {translate("Manufacturing.Planning.rectify", "Rectify")}
                                </Dropdown.Header>
                                {rectify_configs.map(config => (
                                    <Dropdown.Item
                                        key={`${config.strategy}-${config.name}`}
                                        onClick={() => {
                                            this.showRectifyModal(config, true);
                                        }}
                                    >
                                        {config.name}
                                    </Dropdown.Item>
                                ))}
                            </>
                        )}
                        {!is_agrana && backward_planning_configs.length > 0 && (
                            <>
                                <Dropdown.Divider />
                                <Dropdown.Header>
                                    {translate("Manufacturing.Planning.backward_planning", "Backward planning")}
                                </Dropdown.Header>
                                {backward_planning_configs.map(config => (
                                    <Dropdown.Item
                                        key={`${config.strategy}-${config.name}`}
                                        onClick={() => {
                                            this.showBackwardPlanningModal(config, true);
                                        }}
                                    >
                                        {config.name}
                                    </Dropdown.Item>
                                ))}
                            </>
                        )}
                        {!is_agrana && sequencing_planning_configs.length > 0 && (
                            <>
                                <Dropdown.Divider />
                                <Dropdown.Header>
                                    {translate("Manufacturing.Planning.sequencing_planning", "Sequencing planning")}
                                </Dropdown.Header>
                                {sequencing_planning_configs.map(config => (
                                    <Dropdown.Item
                                        key={`${config.strategy}-${config.name}`}
                                        onClick={() => {
                                            this.showSequencingPlanningModal(config, true);
                                        }}
                                    >
                                        {config.name}
                                    </Dropdown.Item>
                                ))}
                            </>
                        )}
                    </Dropdown.Menu>
                </Dropdown>
            </div>
        );
    };

    renderWarnings = () => {
        if (LinesOrdersLogic.hasUnsavedChunks()) {
            return (
                <ErrorComponent
                    className="my-0"
                    type="warning"
                    msg={translate(
                        "Manufacturing.Planning.warning_unsaved_chunk_operations",
                        "Optimization cannot run with unsaved chunk operations"
                    )}
                />
            );
        }

        if (this.props.warnings.length > 0) {
            return (
                <>
                    <h3 className="h5 mb-2">{translate("common.warnings", "Warnings")}</h3>
                    <div className="recommend-warnings alert alert-warning p-0 mb-0">
                        <div className="overflow-auto">
                            <table className="table mb-0">
                                <thead>
                                    <tr>
                                        <th>{translate("common.external_id", "External ID")}</th>
                                        <th>{translate("common.process_num", "Process number")}</th>
                                        <th>{translate("common.warning", "Warning")}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {this.mapWarnings(this.props.warnings).map(warning => {
                                        const { external_id, proces_num, description, explanation, Details } = warning;
                                        return (
                                            <tr key={external_id}>
                                                <td>{external_id}</td>
                                                <td>{proces_num}</td>
                                                <td>
                                                    {description} {explanation}{" "}
                                                    {Details && <Details container_ref={this.modal_ref} />}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </>
            );
        }

        return null;
    };

    renderToolbar = () => {
        return (
            <div className="recommend-plan__toolbar">
                <Select
                    className="react-select react-select--sm w-100"
                    classNamePrefix="react-select"
                    value={this.getSelectedConfigurationOption()}
                    options={this.getConfigurationsOptions()}
                    onChange={value => this.handleConfigurationChange(value)}
                />
                <Authorization.button
                    permission={Auth.PERMISSION_NAMES.SchedulingConfigEdit}
                    className="btn btn-short btn-outline-secondary px-2 ml-2"
                    title={translate("common.save", "Save")}
                    style={{ width: 30 }}
                    disabled={this.state.configuration !== ""}
                    onClick={this.handleConfigurationSave}
                >
                    <i className="fas fa-save align-middle" />
                </Authorization.button>
                <Authorization.button
                    permission={Auth.PERMISSION_NAMES.SchedulingConfigEdit}
                    className="btn btn-short btn-outline-secondary px-2 ml-2"
                    title={translate("common.delete", "Delete")}
                    style={{ width: 30 }}
                    disabled={this.state.configuration === ""}
                    onClick={this.handleConfigurationDelete}
                >
                    <i className="fas fa-trash align-middle" />
                </Authorization.button>
            </div>
        );
    };

    renderLinesTabTitle = (): string => {
        const { lines_orders } = this.props;
        const { selected_lines } = this.state;
        const num_lines = lines_orders.length;
        const num_selected_lines = selected_lines.size;

        const base_title = translate("common.lines", "Lines");
        return num_selected_lines > 0 ? `${base_title} (${num_selected_lines}/${num_lines})` : base_title;
    };

    renderLinesTab = () => {
        return (
            <Tab eventKey="lines" title={this.renderLinesTabTitle()} tabClassName="nav-item">
                <MultiSelect
                    className="recommend-plan__lines-select"
                    options={this.getLinesOptions()}
                    value={this.getSelectedLinesOptions()}
                    filterOption={filterLineOption}
                    placeholder={`${translate("common.search", "Search")}...`}
                    isClearable={false}
                    enable_select_all={true}
                    is_static={true}
                    show_count={false}
                    onChange={this.handleLineSelect}
                    aria-label={translate("common.lines", "Lines")}
                />
            </Tab>
        );
    };

    handleAlternativesStrategyChange = (option: ILabelValuePair) => {
        const strategy = this.state.recommend_plan_parameters.recommend_strategy;
        setRecommendPlanConfigurationValue(
            this.props.line_group_uuid,
            strategy,
            "recommend_param_alternatives_strategy",
            option.value as AlternativesStrategyOption
        );
        this.resetConfiguration();
        this.setState({
            recommend_plan_parameters: getRecommendPlanConfiguration(this.props.line_group_uuid, strategy),
        });
    };

    handleRunUntilPhaseChange = (option: ILabelValuePair) => {
        const strategy = this.state.recommend_plan_parameters.recommend_strategy;
        setRecommendPlanConfigurationValue(
            this.props.line_group_uuid,
            strategy,
            "recommend_param_run_until_phase",
            option.value as PhaseOption
        );
        this.resetConfiguration();
        this.setState({
            recommend_plan_parameters: getRecommendPlanConfiguration(this.props.line_group_uuid, strategy),
        });
    };

    render() {
        const show_customer_priority = BusinessLogic.getLineGroupTagBool(
            this.props.line_group_uuid,
            LINE_GROUP_TAGS.planning_track_customer_priorty,
            false
        );
        const show_track_stock_levels = BusinessLogic.getLineGroupTagBool(
            this.props.line_group_uuid,
            LINE_GROUP_TAGS.planning_track_stock_levels,
            false
        );
        const show_meta_optimization = BusinessLogic.getLineGroupTagBool(
            this.props.line_group_uuid,
            LINE_GROUP_TAGS.planning_meta_optimization_param,
            false
        );
        const recommend_plan_parameters = this.state.recommend_plan_parameters;
        const time_horizont_shifts = recommend_plan_parameters.recommend_param_time_horizont_shifts;
        const selected_configuration = this.getSelectedConfigurationOption();
        const strategy = recommend_plan_parameters.recommend_strategy;
        const is_recommend = strategy !== PLANNING_SCHEDULE_STRATEGY.rectify;
        const is_backward_planning = strategy === PLANNING_SCHEDULE_STRATEGY.backward_planning;
        const is_sequencing_planning = strategy === PLANNING_SCHEDULE_STRATEGY.sequencing_planning;
        const selected_tab = this.state.selected_tab ? this.state.selected_tab : "parameters";
        const is_recommend_running = this.getRecommendIsRunning();
        const has_warnings = this.hasWarnings();
        const curent_time_ts = Date.now();

        return (
            <div className="recommend-plan">
                {this.renderDropdown()}

                <Modal
                    className="recommend-plan__modal"
                    dialogClassName="modal-dialog-scrollable"
                    show={this.getShowModal()}
                    onHide={this.hideConfigurationModal}
                    animation={false}
                >
                    <Modal.Header>
                        <Modal.Title>
                            {strategy === PLANNING_SCHEDULE_STRATEGY.rectify
                                ? translate("Manufacturing.Planning.rectify", "Rectify")
                                : is_backward_planning
                                  ? translate("Manufacturing.Planning.backward_planning", "Backward planning")
                                  : is_sequencing_planning
                                    ? translate("Manufacturing.Planning.sequencing_planning", "Sequencing planning")
                                    : translate("Manufacturing.Planning.recommend", "Recommend")}
                            {this.state.configuration && ` - ${this.state.configuration}`}
                        </Modal.Title>
                        <button type="button" className="close" onClick={this.hideConfigurationModal}>
                            <span aria-hidden="true">×</span>
                            <span className="sr-only">{translate("common.close", "Close")}</span>
                        </button>
                    </Modal.Header>
                    {!is_recommend_running && !has_warnings && this.renderToolbar()}
                    <Modal.Body className="py-3">
                        <div
                            ref={ref => {
                                if (ref !== null) {
                                    this.modal_ref = ref.closest(".modal[role=dialog]") || null;
                                }
                            }}
                        />
                        {this.props.error && (
                            <ErrorComponent
                                className="mt-0 mb-3"
                                msg={
                                    <span title={this.props.error}>
                                        {translate(
                                            "common.automatic_planning_failed",
                                            "Automatic planning failed. Please review your settings and try again."
                                        )}
                                    </span>
                                }
                                type="error"
                                onClose={() => {
                                    this.props.reduxDispatch({ type: SET_RECOMMEND_PLAN_ERROR, data: null });
                                }}
                                onlyDiv={true}
                                is_closable={true}
                            />
                        )}
                        {is_recommend_running ? (
                            <h3 className="text-center">
                                <FormattedMessage
                                    id="Manufacturing.Planning.calculating"
                                    defaultMessage="Calculating"
                                />
                                ...
                            </h3>
                        ) : has_warnings ? (
                            this.renderWarnings()
                        ) : is_backward_planning ? (
                            <>
                                <FormGroup className="d-flex align-items-center justify-content-between mb-2">
                                    <Form.Label className="mb-0" htmlFor="recommend_param_consider_alternative_lines">
                                        <FormattedMessage
                                            id="Manufacturing.Planning.param_consider_alternative_lines"
                                            defaultMessage="Consider alternative lines"
                                        />
                                    </Form.Label>
                                    <Toggle
                                        id="recommend_param_consider_alternative_lines"
                                        checked={recommend_plan_parameters.recommend_param_consider_alternative_lines}
                                        onChange={this.handleRecommendParamToggle}
                                    />
                                </FormGroup>
                                <FormGroup className="d-flex align-items-center justify-content-between mb-2">
                                    <Form.Label className="mb-0" htmlFor="recommend_param_consider_priority">
                                        <FormattedMessage
                                            id="Manufacturing.Planning.param_consider_priority"
                                            defaultMessage="Consider ABC priority"
                                        />
                                    </Form.Label>
                                    <Toggle
                                        id="recommend_param_consider_priority"
                                        checked={recommend_plan_parameters.recommend_param_consider_priority}
                                        onChange={this.handleRecommendParamToggle}
                                    />
                                </FormGroup>
                                <FormGroup className="d-flex align-items-center justify-content-between mb-2">
                                    <Form.Label className="mb-0" htmlFor="recommend_param_consider_people_presences">
                                        <FormattedMessage
                                            id="Manufacturing.Planning.param_consider_people_presences"
                                            defaultMessage="Consider people presences"
                                        />
                                    </Form.Label>
                                    <Toggle
                                        id="recommend_param_consider_people_presences"
                                        checked={recommend_plan_parameters.recommend_param_consider_people_presences}
                                        onChange={this.handleRecommendParamToggle}
                                    />
                                </FormGroup>
                                <FormGroup className="d-flex align-items-center justify-content-between mb-2">
                                    <Form.Label className="mb-0" htmlFor="recommend_param_consider_people_competences">
                                        <FormattedMessage
                                            id="Manufacturing.Planning.param_consider_people_competences"
                                            defaultMessage="Consider people competences"
                                        />
                                    </Form.Label>
                                    <Toggle
                                        id="recommend_param_consider_people_competences"
                                        checked={recommend_plan_parameters.recommend_param_consider_people_competences}
                                        onChange={this.handleRecommendParamToggle}
                                    />
                                </FormGroup>

                                <FormGroup className="d-flex align-items-center justify-content-between mb-2">
                                    <Form.Label className="mb-0" htmlFor="recommend_param_plan_horizont">
                                        {this.getPlanHorizontMsg()}
                                    </Form.Label>
                                    <input
                                        id="recommend_param_plan_horizont"
                                        className="form-control form-control-sm"
                                        style={{ width: 80, height: 30 }}
                                        type="number"
                                        value={
                                            isWeekUnit(this.props.line_group_uuid)
                                                ? Math.round(time_horizont_shifts / 21)
                                                : time_horizont_shifts
                                        }
                                        onChange={event =>
                                            this.handleParamInput("recommend_param_time_horizont_shifts", 1, event)
                                        }
                                        onFocus={selectInputText}
                                    />
                                </FormGroup>
                            </>
                        ) : is_sequencing_planning ? (
                            <Tabs
                                activeKey={selected_tab}
                                className="tabs"
                                id="sequencing-planning-tabs"
                                onSelect={this.handleTabSelect}
                                animation={false}
                            >
                                <Tab
                                    eventKey="parameters"
                                    title={translate("Manufacturing.Planning.parameters", "Parameters")}
                                    tabClassName="nav-item"
                                >
                                    <FormGroup className="d-flex align-items-center justify-content-between mb-2">
                                        <Form.Label className="mb-0" htmlFor="recommend_param_max_order_end_date">
                                            <FormattedMessage
                                                id="Manufacturing.Planning.param_max_order_end_date"
                                                defaultMessage="Max. order end date"
                                            />
                                        </Form.Label>
                                        <DateTimePicker
                                            inputId="recommend_param_max_order_end_date"
                                            name="max_order_end_date"
                                            value={
                                                new Date(
                                                    curent_time_ts +
                                                        (recommend_plan_parameters.recommend_param_max_order_end_date ??
                                                            PLANNING_PARAMS_DEFAULT.max_order_end_date)
                                                )
                                            }
                                            minValue={new Date(curent_time_ts)}
                                            pickerType="date"
                                            popupPostion="fixed"
                                            onChange={date => {
                                                const max_order_end_date = date.getTime() - curent_time_ts;
                                                const strategy =
                                                    this.state.recommend_plan_parameters.recommend_strategy;
                                                setRecommendPlanConfigurationValue(
                                                    this.props.line_group_uuid,
                                                    strategy,
                                                    "recommend_param_max_order_end_date",
                                                    max_order_end_date
                                                );
                                                this.resetConfiguration();
                                                this.setState({
                                                    recommend_plan_parameters: getRecommendPlanConfiguration(
                                                        this.props.line_group_uuid,
                                                        strategy
                                                    ),
                                                });
                                            }}
                                        />
                                    </FormGroup>
                                    <FormGroup className="d-flex align-items-center justify-content-between mb-2">
                                        <Form.Label className="mb-0" htmlFor="recommend_param_horizon_limit">
                                            <FormattedMessage
                                                id="Manufacturing.Planning.param_plan_horizont_limit"
                                                defaultMessage="Plan horizon limit"
                                            />
                                        </Form.Label>
                                        <DateTimePicker
                                            inputId="recommend_param_horizon_limit"
                                            name="horizon_limit"
                                            value={
                                                new Date(
                                                    curent_time_ts +
                                                        (recommend_plan_parameters.recommend_param_horizon_limit ??
                                                            PLANNING_PARAMS_DEFAULT.horizon_limit)
                                                )
                                            }
                                            minValue={new Date(curent_time_ts)}
                                            pickerType="date"
                                            popupPostion="fixed"
                                            onChange={date => {
                                                const horizon_limit = date.getTime() - curent_time_ts;
                                                const strategy =
                                                    this.state.recommend_plan_parameters.recommend_strategy;
                                                setRecommendPlanConfigurationValue(
                                                    this.props.line_group_uuid,
                                                    strategy,
                                                    "recommend_param_horizon_limit",
                                                    horizon_limit
                                                );
                                                this.resetConfiguration();
                                                this.setState({
                                                    recommend_plan_parameters: getRecommendPlanConfiguration(
                                                        this.props.line_group_uuid,
                                                        strategy
                                                    ),
                                                });
                                            }}
                                        />
                                    </FormGroup>
                                    <FormGroup className="d-flex align-items-center justify-content-between mb-2">
                                        <Form.Label className="mb-0" htmlFor="recommend_param_alternatives_strategy">
                                            <FormattedMessage
                                                id="Manufacturing.Planning.param_alternatives_strategy"
                                                defaultMessage="Alternatives strategy"
                                            />
                                        </Form.Label>
                                        <Select
                                            id="recommend_param_alternatives_strategy"
                                            value={this.getSelectedAlternativesStrategy()}
                                            options={this.selectAlternativesStrategyOptions}
                                            onChange={this.handleAlternativesStrategyChange}
                                            menuPortalTarget={document.body}
                                            menuPosition="fixed"
                                            styles={{
                                                control: base => ({ ...base, minWidth: 200 }),
                                                menuPortal: base => ({
                                                    ...base,
                                                    zIndex: 9999,
                                                }),
                                            }}
                                        />
                                    </FormGroup>
                                    <FormGroup className="d-flex align-items-center justify-content-between mb-2">
                                        <Form.Label className="mb-0" htmlFor="recommend_param_include_history">
                                            <FormattedMessage
                                                id="Manufacturing.Planning.param_include_history"
                                                defaultMessage="Include history"
                                            />
                                        </Form.Label>
                                        <Toggle
                                            id="recommend_param_include_history"
                                            checked={recommend_plan_parameters.recommend_param_include_history ?? false}
                                            onChange={this.handleRecommendParamToggle}
                                        />
                                    </FormGroup>
                                    <FormGroup className="d-flex align-items-center justify-content-between mb-2">
                                        <Form.Label className="mb-0" htmlFor="recommend_param_consider_optimum_batch">
                                            <FormattedMessage
                                                id="Manufacturing.Planning.param_consider_optimum_batch"
                                                defaultMessage="Consider optimum batch"
                                            />
                                        </Form.Label>
                                        <Toggle
                                            id="recommend_param_consider_optimum_batch"
                                            checked={
                                                recommend_plan_parameters.recommend_param_consider_optimum_batch ??
                                                false
                                            }
                                            onChange={this.handleRecommendParamToggle}
                                        />
                                    </FormGroup>
                                    <FormGroup className="d-flex align-items-center justify-content-between mb-2">
                                        <Form.Label className="mb-0" htmlFor="recommend_param_run_until_phase">
                                            <FormattedMessage
                                                id="Manufacturing.Planning.param_run_until_phase"
                                                defaultMessage="Run until phase"
                                            />
                                        </Form.Label>
                                        <Select
                                            id="recommend_param_run_until_phase"
                                            value={this.getSelectedRunUntilPhase()}
                                            options={this.selectRunUntilPhaseOptions}
                                            onChange={this.handleRunUntilPhaseChange}
                                            menuPortalTarget={document.body}
                                            menuPosition="fixed"
                                            styles={{
                                                control: base => ({ ...base, minWidth: 200 }),
                                                menuPortal: base => ({
                                                    ...base,
                                                    zIndex: 9999,
                                                }),
                                            }}
                                        />
                                    </FormGroup>
                                </Tab>
                                {this.renderLinesTab()}
                            </Tabs>
                        ) : (
                            <Tabs
                                activeKey={selected_tab}
                                className="tabs"
                                id="recommend-params-tabs"
                                onSelect={this.handleTabSelect}
                                animation={false}
                            >
                                <Tab
                                    eventKey="parameters"
                                    title={translate("Manufacturing.Planning.parameters", "Parameters")}
                                    tabClassName="nav-item"
                                >
                                    <div className="tab-content py-3">
                                        {is_recommend && (
                                            <FormGroup className="d-flex align-items-center justify-content-between mb-2">
                                                <Form.Label
                                                    className="mb-0"
                                                    htmlFor="recommend_param_track_input_material"
                                                >
                                                    <FormattedMessage
                                                        id="Manufacturing.Planning.param_track_input_material"
                                                        defaultMessage="Track input materials"
                                                    />
                                                </Form.Label>
                                                <Toggle
                                                    id="recommend_param_track_input_material"
                                                    checked={
                                                        recommend_plan_parameters.recommend_param_track_input_material
                                                    }
                                                    onChange={this.handleRecommendParamToggle}
                                                />
                                            </FormGroup>
                                        )}
                                        {is_recommend && (
                                            <FormGroup className="d-flex align-items-center justify-content-between mb-2">
                                                <Form.Label
                                                    className="mb-0"
                                                    htmlFor="recommend_param_use_productions_of_unscheduled_operations"
                                                >
                                                    <FormattedMessage
                                                        id="Manufacturing.Planning.param_use_productions_of_unscheduled_operations"
                                                        defaultMessage="Track input materials"
                                                    />
                                                </Form.Label>
                                                <Toggle
                                                    id="recommend_param_use_productions_of_unscheduled_operations"
                                                    checked={
                                                        recommend_plan_parameters.recommend_param_use_productions_of_unscheduled_operations
                                                    }
                                                    onChange={this.handleRecommendParamToggle}
                                                />
                                            </FormGroup>
                                        )}
                                        {is_recommend && (
                                            <FormGroup className="d-flex align-items-center justify-content-between mb-2">
                                                <Form.Label
                                                    className="mb-0"
                                                    htmlFor="recommend_param_use_requirements_from_unscheduled_operations"
                                                >
                                                    <FormattedMessage
                                                        id="Manufacturing.Planning.param_use_requirements_from_unscheduled_operations"
                                                        defaultMessage="Track input materials"
                                                    />
                                                </Form.Label>
                                                <Toggle
                                                    id="recommend_param_use_requirements_from_unscheduled_operations"
                                                    checked={
                                                        recommend_plan_parameters.recommend_param_use_requirements_from_unscheduled_operations
                                                    }
                                                    onChange={this.handleRecommendParamToggle}
                                                />
                                            </FormGroup>
                                        )}
                                        {is_recommend && (
                                            <FormGroup className="d-flex align-items-center justify-content-between mb-2">
                                                <Form.Label className="mb-0" htmlFor="recommend_param_use_safety_stock">
                                                    <FormattedMessage
                                                        id="common.include_safety_stocks"
                                                        defaultMessage="Allow usage of safety stock"
                                                    />
                                                </Form.Label>
                                                <Toggle
                                                    id="recommend_param_use_safety_stock"
                                                    checked={recommend_plan_parameters.recommend_param_use_safety_stock}
                                                    onChange={this.handleRecommendParamToggle}
                                                />
                                            </FormGroup>
                                        )}
                                        <FormGroup className="d-flex align-items-center justify-content-between mb-2">
                                            <Form.Label className="mb-0" htmlFor="recommend_param_include_plan_orders">
                                                <FormattedMessage
                                                    id="Manufacturing.Planning.param_plan_orders"
                                                    defaultMessage="Include plan orders"
                                                />
                                            </Form.Label>
                                            <Toggle
                                                id="recommend_param_include_plan_orders"
                                                checked={recommend_plan_parameters.recommend_param_include_plan_orders}
                                                onChange={this.handleRecommendParamToggle}
                                            />
                                        </FormGroup>
                                        {is_recommend && (
                                            <FormGroup className="d-flex align-items-center justify-content-between mb-2">
                                                <Form.Label
                                                    className="mb-0"
                                                    htmlFor="recommend_param_include_unscheduled_orders"
                                                >
                                                    <FormattedMessage
                                                        id="Manufacturing.Planning.param_include_unscheduled_orders"
                                                        defaultMessage="Include unscheduled orders"
                                                    />
                                                </Form.Label>
                                                <Toggle
                                                    id="recommend_param_include_unscheduled_orders"
                                                    checked={
                                                        recommend_plan_parameters.recommend_param_include_unscheduled_orders
                                                    }
                                                    onChange={this.handleRecommendParamToggle}
                                                />
                                            </FormGroup>
                                        )}
                                        {is_recommend && (
                                            <FormGroup className="d-flex align-items-center justify-content-between mb-2">
                                                <Form.Label
                                                    className="mb-0"
                                                    htmlFor="recommend_param_freeze_work_orders"
                                                >
                                                    <FormattedMessage
                                                        id="Manufacturing.Planning.param_freeze_work_orders"
                                                        defaultMessage="Freez work orders"
                                                    />
                                                </Form.Label>
                                                <Toggle
                                                    id="recommend_param_freeze_work_orders"
                                                    checked={
                                                        recommend_plan_parameters.recommend_param_freeze_work_orders
                                                    }
                                                    onChange={this.handleRecommendParamToggle}
                                                />
                                            </FormGroup>
                                        )}
                                        {is_recommend && (
                                            <FormGroup className="d-flex align-items-center justify-content-between mb-2">
                                                <Form.Label
                                                    className="mb-0"
                                                    htmlFor="recommend_param_plan_after_frozen_orders"
                                                >
                                                    <FormattedMessage
                                                        id="Manufacturing.Planning.param_plan_after_frozen_orders"
                                                        defaultMessage="Plan after frozen work orders"
                                                    />
                                                </Form.Label>
                                                <Toggle
                                                    id="recommend_param_plan_after_frozen_orders"
                                                    checked={
                                                        recommend_plan_parameters.recommend_param_plan_after_frozen_orders
                                                    }
                                                    onChange={this.handleRecommendParamToggle}
                                                />
                                            </FormGroup>
                                        )}
                                        {is_recommend && show_customer_priority && (
                                            <FormGroup className="d-flex align-items-center justify-content-between mb-2">
                                                <Form.Label
                                                    className="mb-0"
                                                    htmlFor="recommend_param_customer_priority"
                                                >
                                                    <FormattedMessage
                                                        id="Manufacturing.Planning.param_customer_priority"
                                                        defaultMessage="Account for customer priority"
                                                    />
                                                </Form.Label>
                                                <Toggle
                                                    id="recommend_param_customer_priority"
                                                    checked={
                                                        recommend_plan_parameters.recommend_param_customer_priority
                                                    }
                                                    onChange={this.handleRecommendParamToggle}
                                                />
                                            </FormGroup>
                                        )}
                                        {is_recommend && (
                                            <FormGroup className="d-flex align-items-center justify-content-between mb-2">
                                                <Form.Label className="mb-0" htmlFor="recommend_param_freeze_shifts">
                                                    <FormattedMessage
                                                        id="Manufacturing.Planning.param_freeze_shifts"
                                                        defaultMessage="Freeze shifts"
                                                    />
                                                </Form.Label>
                                                <input
                                                    id="recommend_param_freeze_shifts"
                                                    className="form-control form-control-sm"
                                                    style={{ width: 80, height: 30 }}
                                                    type="number"
                                                    value={recommend_plan_parameters.recommend_param_freeze_shifts}
                                                    onChange={event =>
                                                        this.handleParamInput(
                                                            "recommend_param_freeze_shifts",
                                                            -1,
                                                            event
                                                        )
                                                    }
                                                    onFocus={selectInputText}
                                                />
                                            </FormGroup>
                                        )}
                                        <FormGroup className="d-flex align-items-center justify-content-between mb-2">
                                            <Form.Label className="mb-0" htmlFor="recommend_param_plan_horizont">
                                                {this.getPlanHorizontMsg()}
                                            </Form.Label>
                                            <input
                                                id="recommend_param_plan_horizont"
                                                className="form-control form-control-sm"
                                                style={{ width: 80, height: 30 }}
                                                type="number"
                                                value={
                                                    isWeekUnit(this.props.line_group_uuid)
                                                        ? Math.round(time_horizont_shifts / 21)
                                                        : time_horizont_shifts
                                                }
                                                onChange={event =>
                                                    this.handleParamInput(
                                                        "recommend_param_time_horizont_shifts",
                                                        1,
                                                        event
                                                    )
                                                }
                                                onFocus={selectInputText}
                                            />
                                        </FormGroup>
                                        {is_recommend && (
                                            <>
                                                <h3 className="h5 mt-3 mb-2">
                                                    <FormattedMessage
                                                        id="Manufacturing.Planning.criteria"
                                                        defaultMessage="Criteria"
                                                    />
                                                </h3>
                                                <FormGroup className="d-flex align-items-center justify-content-between mb-2">
                                                    <Form.Label className="mb-0" htmlFor="recommend_criteria_delays">
                                                        <FormattedMessage
                                                            id="Manufacturing.Planning.criteria_delays"
                                                            defaultMessage="Delays"
                                                        />
                                                    </Form.Label>
                                                    <Select
                                                        inputId="recommend_criteria_delays"
                                                        className="react-select react-select--sm"
                                                        classNamePrefix="react-select"
                                                        menuPortalTarget={document.body}
                                                        menuPosition="fixed"
                                                        options={getNumberOptions(0, 6)}
                                                        value={{
                                                            label: `${recommend_plan_parameters.recommend_criteria_delays}`,
                                                            value: recommend_plan_parameters.recommend_criteria_delays,
                                                        }}
                                                        onChange={value =>
                                                            this.handleCriteriaChange(
                                                                "recommend_criteria_delays",
                                                                value
                                                            )
                                                        }
                                                    />
                                                </FormGroup>
                                                <FormGroup className="d-flex align-items-center justify-content-between mb-2">
                                                    <Form.Label
                                                        className="mb-0"
                                                        htmlFor="recommend_criteria_tool_changes"
                                                    >
                                                        <FormattedMessage
                                                            id="Manufacturing.Planning.criteria_tool_changes"
                                                            defaultMessage="Tool changes"
                                                        />
                                                    </Form.Label>
                                                    <Select
                                                        inputId="recommend_criteria_tool_changes"
                                                        className="react-select react-select--sm"
                                                        classNamePrefix="react-select"
                                                        menuPortalTarget={document.body}
                                                        menuPosition="fixed"
                                                        options={getNumberOptions(0, 6)}
                                                        value={{
                                                            label: `${recommend_plan_parameters.recommend_criteria_tool_changes}`,
                                                            value: recommend_plan_parameters.recommend_criteria_tool_changes,
                                                        }}
                                                        onChange={value =>
                                                            this.handleCriteriaChange(
                                                                "recommend_criteria_tool_changes",
                                                                value
                                                            )
                                                        }
                                                    />
                                                </FormGroup>
                                                {show_track_stock_levels && (
                                                    <FormGroup className="d-flex align-items-center justify-content-between mb-2">
                                                        <Form.Label
                                                            className="mb-0"
                                                            htmlFor="recommend_criteria_intermediate_stock"
                                                        >
                                                            <FormattedMessage
                                                                id="Manufacturing.Planning.criteria_intermediate_stock"
                                                                defaultMessage="Value of intermediate stock"
                                                            />
                                                        </Form.Label>
                                                        <Select
                                                            inputId="recommend_criteria_intermediate_stock"
                                                            className="react-select react-select--sm"
                                                            classNamePrefix="react-select"
                                                            menuPortalTarget={document.body}
                                                            menuPosition="fixed"
                                                            options={getNumberOptions(0, 6)}
                                                            value={{
                                                                label: `${recommend_plan_parameters.recommend_criteria_intermediate_stock}`,
                                                                value: recommend_plan_parameters.recommend_criteria_intermediate_stock,
                                                            }}
                                                            onChange={value =>
                                                                this.handleCriteriaChange(
                                                                    "recommend_criteria_intermediate_stock",
                                                                    value
                                                                )
                                                            }
                                                        />
                                                    </FormGroup>
                                                )}
                                            </>
                                        )}
                                    </div>
                                </Tab>
                                {is_recommend && (
                                    <Tab
                                        eventKey="advanced"
                                        title={translate(
                                            "Manufacturing.Planning.advanced_criteria",
                                            "Advanced criteria"
                                        )}
                                        tabClassName="nav-item"
                                    >
                                        <div className="tab-content py-3">
                                            <FormGroup className="d-flex align-items-center justify-content-between mb-2">
                                                <Form.Label
                                                    className="mb-0"
                                                    htmlFor="recommend_param_beam_size"
                                                    title={translate(
                                                        "Manufacturing.Planning.help.beam_size",
                                                        "Size of search-beam in plannnig. Default is 3."
                                                    )}
                                                >
                                                    {translate(
                                                        "Manufacturing.Planning.beam_size",
                                                        "Size of search-beam"
                                                    )}
                                                </Form.Label>
                                                <input
                                                    id="recommend_param_beam_size"
                                                    className="form-control form-control-sm"
                                                    style={{ width: 80, height: 30 }}
                                                    type="number"
                                                    value={recommend_plan_parameters.recommend_param_beam_size}
                                                    onChange={event =>
                                                        this.handleParamInput("recommend_param_beam_size", 1, event)
                                                    }
                                                    onFocus={selectInputText}
                                                />
                                            </FormGroup>
                                            <FormGroup className="d-flex align-items-center justify-content-between mb-2">
                                                <Form.Label
                                                    className="mb-0"
                                                    htmlFor="recommend_param_need_score_factor"
                                                >
                                                    {translate(
                                                        "Manufacturing.Planning.param_need_score_factor",
                                                        "Need sequence factor"
                                                    )}
                                                </Form.Label>
                                                <input
                                                    id="recommend_param_need_score_factor"
                                                    className="form-control form-control-sm"
                                                    style={{ width: 80, height: 30 }}
                                                    type="number"
                                                    value={recommend_plan_parameters.recommend_param_need_score_factor}
                                                    onChange={event =>
                                                        this.handleParamInput(
                                                            "recommend_param_need_score_factor",
                                                            0,
                                                            event
                                                        )
                                                    }
                                                    onFocus={selectInputText}
                                                />
                                            </FormGroup>
                                            <FormGroup className="d-flex align-items-center justify-content-between mb-2">
                                                <Form.Label
                                                    className="mb-0"
                                                    htmlFor="recommend_param_setup_code_change_factor"
                                                >
                                                    {translate(
                                                        "Manufacturing.Planning.param_setup_code_change_factor",
                                                        "Setup code change factor"
                                                    )}
                                                </Form.Label>
                                                <input
                                                    id="recommend_param_setup_code_change_factor"
                                                    className="form-control form-control-sm"
                                                    style={{ width: 80, height: 30 }}
                                                    type="number"
                                                    value={
                                                        recommend_plan_parameters.recommend_param_setup_code_change_factor
                                                    }
                                                    onChange={event =>
                                                        this.handleParamInput(
                                                            "recommend_param_setup_code_change_factor",
                                                            0,
                                                            event
                                                        )
                                                    }
                                                    onFocus={selectInputText}
                                                />
                                            </FormGroup>
                                            <FormGroup className="d-flex align-items-center justify-content-between mb-2">
                                                <Form.Label
                                                    className="mb-0"
                                                    htmlFor="recommend_param_work_duration_factor"
                                                >
                                                    {translate(
                                                        "Manufacturing.Planning.work_duration_weight",
                                                        "Work duration weight"
                                                    )}
                                                </Form.Label>
                                                <input
                                                    id="recommend_param_work_duration_factor"
                                                    className="form-control form-control-sm"
                                                    style={{ width: 80, height: 30 }}
                                                    type="number"
                                                    value={
                                                        recommend_plan_parameters.recommend_param_work_duration_factor
                                                    }
                                                    onChange={event =>
                                                        this.handleParamInput(
                                                            "recommend_param_work_duration_factor",
                                                            0,
                                                            event
                                                        )
                                                    }
                                                    onFocus={selectInputText}
                                                />
                                            </FormGroup>
                                            <FormGroup className="d-flex align-items-center justify-content-between mb-2">
                                                <Form.Label
                                                    className="mb-0"
                                                    htmlFor="recommend_param_can_change_workorder_line"
                                                    title={translate(
                                                        "Manufacturing.Planning.help.can_change_workorder_line",
                                                        "Size of search-beam in plannnig. Default is 3."
                                                    )}
                                                >
                                                    <FormattedMessage
                                                        id="Manufacturing.Planning.can_change_workorder_line"
                                                        defaultMessage="Can workorder change line"
                                                    />
                                                </Form.Label>
                                                <Toggle
                                                    id="recommend_param_can_change_workorder_line"
                                                    checked={
                                                        recommend_plan_parameters.recommend_param_can_change_workorder_line
                                                    }
                                                    onChange={this.handleRecommendParamToggle}
                                                />
                                            </FormGroup>

                                            {show_meta_optimization && (
                                                <>
                                                    <FormGroup className="d-flex align-items-center justify-content-between mb-2">
                                                        <Form.Label
                                                            className="mb-0"
                                                            htmlFor="recommend_param_meta_optimization"
                                                        >
                                                            <FormattedMessage
                                                                id="Manufacturing.Planning.param_meta_optimization"
                                                                defaultMessage="Meta optimization"
                                                            />
                                                        </Form.Label>
                                                        <Select
                                                            inputId="recommend_param_meta_optimization"
                                                            styles={{
                                                                control: provided => ({ ...provided, width: 140 }),
                                                            }}
                                                            theme={theme => ({
                                                                ...theme,
                                                                spacing: {
                                                                    ...theme.spacing,
                                                                    baseUnit: 2,
                                                                    controlHeight: 30,
                                                                },
                                                            })}
                                                            components={{
                                                                IndicatorSeparator: null,
                                                            }}
                                                            value={{
                                                                label: `${recommend_plan_parameters.recommend_param_meta_optimization}`,
                                                                value: recommend_plan_parameters.recommend_param_meta_optimization,
                                                            }}
                                                            options={Object.keys(PLANNING_META_OPTIMIZATION).map(v => ({
                                                                label: `${v}`,
                                                                value: v,
                                                            }))}
                                                            onChange={value => this.handleMetaOptimizationChange(value)}
                                                        />
                                                    </FormGroup>
                                                    {![
                                                        PLANNING_META_OPTIMIZATION.none,
                                                        PLANNING_META_OPTIMIZATION.max_resources,
                                                    ].includes(
                                                        recommend_plan_parameters.recommend_param_meta_optimization as PlanningMetaOptimizationEnum
                                                    ) && (
                                                        <FormGroup className="d-flex align-items-center justify-content-between mb-2">
                                                            <Form.Label
                                                                className="mb-0"
                                                                htmlFor="recommend_param_override_shifts"
                                                            >
                                                                {translate(
                                                                    "Manufacturing.Planning.param_override_shifts",
                                                                    "Override shifts"
                                                                )}
                                                            </Form.Label>
                                                            <input
                                                                id="recommend_param_override_shifts"
                                                                className="form-control form-control-sm"
                                                                style={{ width: 80, height: 30 }}
                                                                type="number"
                                                                value={
                                                                    recommend_plan_parameters.recommend_param_override_shifts
                                                                }
                                                                onChange={event =>
                                                                    this.handleParamInput(
                                                                        "recommend_param_override_shifts",
                                                                        1,
                                                                        event,
                                                                        4
                                                                    )
                                                                }
                                                                onFocus={selectInputText}
                                                            />
                                                        </FormGroup>
                                                    )}
                                                </>
                                            )}
                                        </div>
                                    </Tab>
                                )}
                                {this.renderLinesTab()}
                            </Tabs>
                        )}
                    </Modal.Body>
                    <Modal.Footer>
                        {has_warnings ? (
                            <button
                                onClick={this.hideConfigurationModal}
                                className="btn btn-primary"
                                disabled={is_recommend_running}
                                autoFocus={true}
                            >
                                <FormattedMessage id="common.close" defaultMessage="Close" />
                            </button>
                        ) : (
                            <>
                                <button
                                    onClick={this.hideConfigurationModal}
                                    className="btn btn-outline-secondary"
                                    disabled={is_recommend_running}
                                >
                                    <FormattedMessage id="common.cancel" defaultMessage="Cancel" />
                                </button>
                                <button
                                    className="btn btn-outline-secondary"
                                    onClick={this.onSetDefault}
                                    disabled={is_recommend_running}
                                >
                                    <FormattedMessage id="common.reset" defaultMessage="Reset" />
                                </button>
                                <button
                                    className="btn btn-primary"
                                    onClick={this.handlePlanningRun}
                                    disabled={is_recommend_running}
                                    autoFocus={true}
                                >
                                    <FormattedMessage id="common.run" defaultMessage="Run" />
                                </button>
                            </>
                        )}
                    </Modal.Footer>
                </Modal>
                <SaveConfigurationModal
                    show={this.state.show_file_name_modal}
                    isUnique={this.hasUniqueName}
                    onAccept={this.handleConfigurationSaveConfirm}
                    onCancel={this.handleConfigurationSaveCancel}
                />
                <ConfirmationModal
                    show={this.state.show_delete_modal}
                    title={translate("Manufacturing.Planning.delete_configuration", "Delete configuration")}
                    accept_label={translate("common.delete", "Delete")}
                    cancel_label={translate("common.cancel", "Cancel")}
                    dangerous={true}
                    onAccept={this.handleConfigurationDeleteConfirm}
                    onCancel={this.handleConfigurationDeleteCancel}
                >
                    {translate(
                        "Manufacturing.Planning.are_you_sure_delete_config",
                        "Are you sure you want to delete this configuration?"
                    )}{" "}
                    <strong>{selected_configuration.label}</strong>
                </ConfirmationModal>
            </div>
        );
    }
}

const mapState = (state: ReduxState) => {
    const planning_table = state.gantt_chart_planning_table;
    const recommend_plan = planning_table.recommend_plan;
    const lines_orders = state.gantt_chart_lines_orders.lines_orders;
    const unscheduled_orders = state.gantt_chart_lines_orders.unscheduled_orders;
    const selected_line_uuids = state.gantt_chart_filters.selected_line_uuids;
    const report_loading: boolean = state.gantt_chart_lines_orders.loading;
    const report = state.gantt_chart_report;
    const original_lines_orders = state.gantt_chart_lines_orders.original_lines_orders;
    const original_unscheduled_orders = state.gantt_chart_lines_orders.original_unscheduled_orders;
    const properties = state.gantt_chart_properties;

    return {
        lines_orders,
        unscheduled_orders,
        selected_line_uuids,
        properties,
        report_loading,
        report,
        error: recommend_plan.error,
        original_lines_orders,
        original_unscheduled_orders,
        warnings: recommend_plan.warnings,
        show_recommend_param_dialog: recommend_plan.show_recommend_param_dialog,
        recommend_plan_params: recommend_plan.recommend_plan_params,
        recommend_running: recommend_plan.recommend_running,
    };
};

const mapDispatch = (dispatch: Dispatch<ActionTypes>) => ({ reduxDispatch: dispatch });

type ReduxProps = ReturnType<typeof mapState> & ReturnType<typeof mapDispatch>;

export default connect<ReturnType<typeof mapState>, ReturnType<typeof mapDispatch>, OwnProps, ReduxState>(
    mapState,
    mapDispatch
)(RecommendPlan);
